import React, { useState, useEffect, useCallback, useMemo, useRef, useId } from 'react';
import { useAuth } from '../context/AuthContext';
import { filterEnabledPlatforms } from '../config/disabledPlatforms';
import PlatformIcon from './PlatformIcon';
import ComposePostModal from './ComposePostModal';

const API = process.env.REACT_APP_API_URL || 'https://api.wintaibot.com';

/**
 * Backend stores dates as Java LocalDateTime in UTC without a timezone suffix.
 * JavaScript's `new Date("2026-04-16T01:00:00")` treats bare ISO strings as
 * LOCAL time, which shifts the date by the user's UTC offset. Appending "Z"
 * tells the browser the timestamp is UTC so it converts to local correctly.
 */
function parseUtcDate(raw) {
  if (!raw) return new Date(NaN);
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}T/.test(s) && !s.includes('Z') && !s.includes('+') && !/T.*-/.test(s)) {
    return new Date(s + 'Z');
  }
  return new Date(s);
}

const PLATFORMS = filterEnabledPlatforms([
  { id: 'instagram', label: 'Instagram', color: '#E1306C', emoji: '📸', logo: 'instagram' },
  { id: 'facebook',  label: 'Facebook',  color: '#1877F2', emoji: '👍', logo: 'facebook'  },
  { id: 'youtube',   label: 'YouTube',   color: '#FF0000', emoji: '▶️', logo: 'youtube'   },
  { id: 'tiktok',    label: 'TikTok',    color: '#010101', emoji: '🎵', logo: 'tiktok'    },
  { id: 'linkedin',  label: 'LinkedIn',  color: '#0A66C2', emoji: '💼', logo: 'linkedin'  },
  { id: 'x',         label: 'X',         color: '#000000', emoji: '🐦', logo: 'x'         },
  { id: 'threads',   label: 'Threads',   color: '#101010', emoji: '🧵', logo: 'threads'   },
  { id: 'pinterest', label: 'Pinterest', color: '#E60023', emoji: '📌', logo: 'pinterest' },
]);

const PLATFORM_COLORS = {
  instagram: '#E1306C', facebook: '#1877F2', youtube: '#FF0000',
  tiktok: '#010101', linkedin: '#0A66C2', x: '#000000',
  threads: '#101010', pinterest: '#E60023',
};

const DAY_LABELS      = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const DAY_FULL_LABELS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const HOUR_LABELS     = Array.from({ length: 24 }, (_, i) =>
  i === 0 ? '12a' : i < 12 ? `${i}a` : i === 12 ? '12p' : `${i-12}p`);

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Section tabs (not platform tabs) — serial shown in UI
const SECTION_TABS = [
  { id: 'growth',     serial: '1', label: '👥 Follower Growth' },
  { id: 'besttime',   serial: '2', label: '⏰ Best time to post' },
  { id: 'breakdown',  serial: '3', label: '📊 Breakdown' },
  { id: 'competitor', serial: '4', label: '🔍 Competitor' },
  { id: 'calendar',   serial: '5', label: '📅 Calendar' },
];

const ALL_PLATFORM_SCOPE = { id: 'all', label: 'All platforms', color: '#6366f1', emoji: '🌐', logo: 'globe' };

/** Figma-style platform scope: labeled control, floating menu, icons, keyboard + mouse parity */
function PlatformScopeMenu({ value, onChange }) {
  const listboxId = useId();
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const itemRefs = useRef([]);
  const highlightedIndexRef = useRef(0);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [triggerHover, setTriggerHover] = useState(false);
  highlightedIndexRef.current = highlightedIndex;

  const options = useMemo(() => [ALL_PLATFORM_SCOPE, ...PLATFORMS], []);
  const selectedIndex = useMemo(
    () => Math.max(0, options.findIndex((o) => o.id === value)),
    [options, value],
  );
  const current =
    value === 'all' ? ALL_PLATFORM_SCOPE : PLATFORMS.find((p) => p.id === value) || ALL_PLATFORM_SCOPE;

  const close = useCallback(() => {
    setOpen(false);
    setTimeout(() => triggerRef.current?.focus(), 0);
  }, []);

  const selectByIndex = useCallback(
    (idx) => {
      const opt = options[idx];
      if (!opt) return;
      // Close the dropdown first — let React commit that unmount cleanly.
      setOpen(false);
      // Defer the parent state until after the menu paint so the listbox
      // DOM is fully torn down (setTimeout(0) alone can still interleave
      // with the Growth Planner chart commit and trigger removeChild).
      const run = () => {
        onChange(opt.id);
        triggerRef.current?.focus();
      };
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => {
          requestAnimationFrame(run);
        });
      } else {
        setTimeout(run, 0);
      }
    },
    [onChange, options],
  );

  useEffect(() => {
    if (open) setHighlightedIndex(selectedIndex);
  }, [open, selectedIndex]);

  useEffect(() => {
    if (!open) return undefined;
    const el = itemRefs.current[highlightedIndex];
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [highlightedIndex, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) close();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, close]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        close();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setHighlightedIndex((i) => (i + 1) % options.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setHighlightedIndex((i) => (i - 1 + options.length) % options.length);
        return;
      }
      if (e.key === 'Home') {
        e.preventDefault();
        e.stopPropagation();
        setHighlightedIndex(0);
        return;
      }
      if (e.key === 'End') {
        e.preventDefault();
        e.stopPropagation();
        setHighlightedIndex(options.length - 1);
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        selectByIndex(highlightedIndexRef.current);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, close, options.length, selectByIndex]);

  const onTriggerKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        setHighlightedIndex(selectedIndex);
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (open) return;
      setHighlightedIndex(selectedIndex);
      setOpen(true);
      return;
    }
    if (e.key === 'Escape' && open) {
      e.preventDefault();
      close();
    }
  };

  const toggleOpen = () => {
    if (open) {
      setOpen(false);
      return;
    }
    setHighlightedIndex(selectedIndex);
    setOpen(true);
  };

  const maxH = typeof window !== 'undefined' ? Math.min(320, window.innerHeight - 140) : 320;

  const triggerBase = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    minHeight: 32,
    paddingLeft: 8,
    paddingRight: 8,
    borderRadius: 8,
    border: `1px solid ${open || triggerHover ? 'rgba(99, 102, 241, 0.45)' : '#e2e8f0'}`,
    background: open ? '#fafaff' : '#ffffff',
    boxShadow: open
      ? '0 0 0 3px rgba(99, 102, 241, 0.18), 0 4px 14px rgba(15, 23, 42, 0.08)'
      : '0 1px 2px rgba(15, 23, 42, 0.05)',
    cursor: 'pointer',
    fontFamily: "'Inter', -apple-system, sans-serif",
    fontSize: 12,
    fontWeight: 600,
    color: '#0f172a',
    letterSpacing: '-0.01em',
    outline: 'none',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
  };

  const menuSurface = {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: 0,
    minWidth: 280,
    maxHeight: maxH,
    overflowY: 'auto',
    padding: '8px 6px 6px',
    background: '#ffffff',
    borderRadius: 12,
    border: '1px solid #e2e8f0',
    boxShadow: '0 16px 48px rgba(15, 23, 42, 0.14), 0 0 0 1px rgba(15, 23, 42, 0.04)',
    zIndex: 200,
  };

  return (
    <div
      ref={wrapRef}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        flexShrink: 0,
        position: 'relative',
        fontFamily: "'Inter', -apple-system, sans-serif",
      }}
    >
      <div style={{ position: 'relative' }}>
        <button
          ref={triggerRef}
          type="button"
          id={`${listboxId}-trigger`}
          onClick={toggleOpen}
          onKeyDown={onTriggerKeyDown}
          onMouseEnter={() => setTriggerHover(true)}
          onMouseLeave={() => setTriggerHover(false)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-label="Filter by platform"
          style={triggerBase}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 20,
              height: 20,
              borderRadius: 6,
              background: '#f1f5f9',
            }}
          >
            <PlatformIcon platform={current} size={14} />
          </span>
          <span style={{ maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>
            {current.label}
          </span>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden
            style={{
              flexShrink: 0,
              color: '#64748b',
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s ease',
            }}
          >
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {open && (
          <div
            id={listboxId}
            role="listbox"
            aria-label="Platform"
            style={menuSurface}
          >
            <div
              style={{
                padding: '4px 10px 8px',
                borderBottom: '1px solid #f1f5f9',
                marginBottom: 4,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>Filter by platform</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, lineHeight: 1.35 }}>
                Applies to charts and metrics below. Use arrow keys, then Enter.
              </div>
            </div>
            {options.map((opt, idx) => {
              const selected = value === opt.id;
              const highlighted = idx === highlightedIndex;
              const rowBg = selected
                ? 'rgba(99, 102, 241, 0.1)'
                : highlighted
                  ? '#f1f5f9'
                  : 'transparent';
              return (
                <button
                  key={opt.id}
                  ref={(el) => {
                    itemRefs.current[idx] = el;
                  }}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  onClick={() => selectByIndex(idx)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 10px',
                    marginBottom: 2,
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: 13,
                    fontWeight: selected ? 600 : 500,
                    color: selected ? '#3730a3' : '#334155',
                    background: rowBg,
                    boxShadow: selected ? 'inset 0 0 0 1px rgba(99, 102, 241, 0.25)' : 'none',
                    transition: 'background 0.1s ease, color 0.1s ease',
                  }}
                >
                  <span
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: '#ffffff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
                      border: '1px solid #f1f5f9',
                    }}
                  >
                    <PlatformIcon platform={opt} size={20} />
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>{opt.label}</span>
                  {selected ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden style={{ flexShrink: 0, color: '#6366f1' }}>
                      <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                    </svg>
                  ) : (
                    <span
                      aria-hidden
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        border: highlighted ? '1.5px solid #94a3b8' : '1.5px solid #e2e8f0',
                        flexShrink: 0,
                        boxSizing: 'border-box',
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function formatHour12(hour) {
  const h = Number(hour);
  if (Number.isNaN(h)) return '';
  if (h === 0) return '12:00 AM';
  if (h < 12) return `${h}:00 AM`;
  if (h === 12) return '12:00 PM';
  return `${h - 12}:00 PM`;
}

function fmtNum(n) {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function hexToRgb(hex) {
  const h = (hex || '#6366f1').replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return { r: 99, g: 102, b: 241 };
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/** Lighter mixes of platform brand for the three type towers (video = full brand). */
function typeTowerColors(platformHex) {
  const { r, g, b } = hexToRgb(platformHex);
  const mix = (t) => {
    const R = Math.round(r + (255 - r) * t);
    const G = Math.round(g + (255 - g) * t);
    const B = Math.round(b + (255 - b) * t);
    return `rgb(${R},${G},${B})`;
  };
  return { video: mix(0), image: mix(0.32), text: mix(0.58) };
}

/** Pale track behind tower fill — same hue family as the selected platform (e.g. Facebook = light blue). */
function brandTowerTrackBg(platformHex) {
  const { r, g, b } = hexToRgb(platformHex);
  const t = 0.86;
  return `rgb(${Math.round(r + (255 - r) * t)},${Math.round(g + (255 - g) * t)},${Math.round(b + (255 - b) * t)})`;
}

/* ── SVG Line Chart ─────────────────────────────────────────── */
function LineChart({ data, color = '#6366f1', width = 500, height = 160 }) {
  const gradId = useId().replace(/:/g, '');

  if (!data || data.length === 0) {
    return (
      <div style={{ height, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    gap: 8, color: '#64748b', fontSize: 13, background: '#f8fafc', borderRadius: 12, padding: '16px 20px', textAlign: 'center' }}>
        <span style={{ fontSize: 22, opacity: 0.35 }}>📈</span>
        <div><strong style={{ color: '#475569' }}>Follower trend chart</strong></div>
        <div style={{ maxWidth: 360, lineHeight: 1.5 }}>
          No history points yet. Snapshots are saved daily (3am UTC) or when you use <strong>Refresh Now</strong> — after two or more points, your line appears here.
          If data stopped updating, your platform token may have expired — reconnect in <strong>Social Connect</strong>.
        </div>
      </div>
    );
  }

  if (data.length === 1) {
    const d0 = data[0];
    const f = d0.followers ?? 0;
    return (
      <div style={{
        height, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 10, background: '#f8fafc', borderRadius: 12, padding: '16px 20px', textAlign: 'center',
        border: `1px dashed ${color}55`,
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>One snapshot so far</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 14, height: 14, borderRadius: '50%', background: color, boxShadow: `0 0 0 4px ${color}33` }} />
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 20, fontWeight: 800, color }}>{f.toLocaleString()}</div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>followers · {d0.date || 'latest'}</div>
          </div>
        </div>
        <div style={{ fontSize: 12, color: '#64748b', maxWidth: 340, lineHeight: 1.5 }}>
          A line needs at least <strong>two</strong> days of snapshots. Run <strong>Refresh Now</strong> again tomorrow or wait for the next scheduled snapshot.
        </div>
      </div>
    );
  }

  const vals = data.map(d => d.followers);
  const min  = Math.min(...vals);
  const max  = Math.max(...vals);
  const range = max - min || 1;

  const pad = { top: 16, right: 12, bottom: 28, left: 48 };
  const W = width - pad.left - pad.right;
  const H = height - pad.top - pad.bottom;

  const x = (i) => pad.left + (i / (data.length - 1)) * W;
  const y = (v) => pad.top + H - ((v - min) / range) * H;

  const points = data.map((d, i) => `${x(i)},${y(d.followers)}`).join(' ');
  const area   = `${x(0)},${y(min) + H} ` + data.map((d, i) => `${x(i)},${y(d.followers)}`).join(' ') + ` ${x(data.length - 1)},${y(min) + H}`;

  const yTicks  = [0, 0.25, 0.5, 0.75, 1].map(t => min + t * range);
  const step    = Math.max(1, Math.floor(data.length / 5));
  const xLabels = data.filter((_, i) => i % step === 0 || i === data.length - 1);

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{ overflow: 'visible' }}>
      <defs>
        <linearGradient id={`${gradId}-areaGrad`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0.03" />
        </linearGradient>
      </defs>
      {yTicks.map((v, i) => (
        <g key={i}>
          <line x1={pad.left} y1={y(v)} x2={pad.left + W} y2={y(v)}
                stroke="#e2e8f0" strokeWidth="1" strokeDasharray="3,3" />
          <text x={pad.left - 6} y={y(v) + 4} textAnchor="end"
                fontSize="10" fill="#94a3b8">
            {v >= 1000 ? `${(v/1000).toFixed(1)}k` : Math.round(v)}
          </text>
        </g>
      ))}
      <polygon points={area} fill={`url(#${gradId}-areaGrad)`} />
      <polyline points={points} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" />
      {data.length <= 15 && data.map((d, i) => (
        <circle key={i} cx={x(i)} cy={y(d.followers)} r="3.5" fill={color} />
      ))}
      {xLabels.map((d) => {
        const idx = data.indexOf(d);
        const dateStr = typeof d?.date === 'string' ? d.date : '';
        return (
          <text key={dateStr || idx} x={x(idx)} y={height - 4}
                textAnchor="middle" fontSize="9" fill="#94a3b8">
            {dateStr.slice(5)}
          </text>
        );
      })}
    </svg>
  );
}

/* ── Best time: normalize API + client fallbacks + guidance ───────── */

function padHeatmapTo7x24(hm) {
  if (!hm || !Array.isArray(hm) || hm.length === 0) {
    return Array.from({ length: 7 }, () => Array(24).fill(0));
  }
  const rows = [];
  for (let d = 0; d < 7; d++) {
    const src = d < hm.length && Array.isArray(hm[d]) ? hm[d] : [];
    const row = [];
    for (let h = 0; h < 24; h++) {
      row.push(Number(src[h]) || 0);
    }
    rows.push(row);
  }
  return rows;
}

function isHeatmapAllZeros(rows) {
  return !rows || rows.every((r) => r.every((c) => !Number(c)));
}

/** When API heatmap is empty but postsDetail has timestamps, build Mon=0 … Sun=6 grid. */
function buildHeatmapFromPostsDetail(postsDetail) {
  const grid = Array.from({ length: 7 }, () => Array(24).fill(0));
  let n = 0;
  (postsDetail || []).forEach((p) => {
    let d = p.dayIndex;
    let h = p.hour;
    if (d == null || h == null) {
      const raw = p.createdAt || p.publishedAt || p.dateTime;
      if (!raw) return;
      // Backend stores dates in UTC without timezone suffix. Append "Z" so
      // JavaScript converts to local time — otherwise getHours() returns the
      // UTC hour (e.g. 1 AM UTC instead of 9 PM local), skewing the heatmap.
      const str = String(raw);
      const utcAware = (/^\d{4}-\d{2}-\d{2}T/.test(str) && !str.includes('Z') && !str.includes('+') && !/T.*-/.test(str))
        ? str + 'Z' : str;
      const dt = new Date(utcAware);
      if (Number.isNaN(dt.getTime())) return;
      d = (dt.getDay() + 6) % 7;
      h = dt.getHours();
    } else {
      d = Number(d);
      h = Number(h);
    }
    if (d < 0 || d > 6 || h < 0 || h > 23) return;
    grid[d][h] += 1;
    n += 1;
  });
  return n > 0 ? grid : null;
}

function inferPeakFromHeatmap(heatmap) {
  let bestD = 0;
  let bestH = 0;
  let bestC = -1;
  for (let d = 0; d < 7; d++) {
    for (let h = 0; h < 24; h++) {
      const c = Number(heatmap[d]?.[h]) || 0;
      if (c > bestC) {
        bestC = c;
        bestD = d;
        bestH = h;
      }
    }
  }
  if (bestC <= 0) return null;
  return { d: bestD, h: bestH, count: bestC };
}

function topSlotsFromHeatmap(heatmap, limit) {
  const arr = [];
  for (let d = 0; d < 7; d++) {
    for (let h = 0; h < 24; h++) {
      const c = Number(heatmap[d]?.[h]) || 0;
      if (c > 0) arr.push({ d, h, c });
    }
  }
  arr.sort((a, b) => b.c - a.c);
  return arr.slice(0, limit);
}

/**
 * Single source of truth for heatmap + best day/hour (fills gaps if API omits bestDay/bestHour).
 */
function resolveBestTimeView(api) {
  if (!api || typeof api !== 'object') return null;
  const rawPadded = padHeatmapTo7x24(api.heatmap);
  let heatmap = rawPadded;
  const postsDetail = api.postsDetail || [];
  let inferredFromDetail = false;
  if (isHeatmapAllZeros(heatmap)) {
    const built = buildHeatmapFromPostsDetail(postsDetail);
    if (built) {
      heatmap = built;
      inferredFromDetail = true;
    }
  }
  if (isHeatmapAllZeros(heatmap)) return null;

  const peak = inferPeakFromHeatmap(heatmap);
  if (!peak) return null;

  let bestDay = api.bestDay;
  let bestHour =
    api.bestHour != null && api.bestHour !== ''
      ? Number(api.bestHour)
      : null;
  if (bestHour != null && Number.isNaN(bestHour)) bestHour = null;

  let bestDayIndex = bestDay
    ? DAY_LABELS.findIndex((dl) => String(bestDay).toLowerCase().startsWith(dl.toLowerCase()))
    : -1;

  if (bestHour == null) bestHour = peak.h;
  if (bestDayIndex < 0) bestDayIndex = peak.d;
  if (!bestDay) bestDay = DAY_FULL_LABELS[peak.d];

  const totalPosts = heatmap.flat().reduce((a, b) => a + Number(b || 0), 0);
  const topSlots = topSlotsFromHeatmap(heatmap, 5);

  return {
    heatmap,
    bestDay,
    bestHour,
    bestDayIndex,
    totalPosts,
    topSlots,
    peakCount: peak.count,
    inferredFromDetail,
  };
}

function BestTimeGuidancePanel({ resolved, platformLabel, platformColor }) {
  const tz =
    typeof Intl !== 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone || 'your local time'
      : 'your local time';
  const extras = (resolved.topSlots || []).filter(
    (s) => !(s.d === resolved.bestDayIndex && s.h === resolved.bestHour)
  );

  return (
    <div
      style={{
        marginBottom: 20,
        padding: '18px 20px',
        borderRadius: 16,
        border: `2px solid ${platformColor}40`,
        background: `linear-gradient(135deg, ${platformColor}12, #fff 55%)`,
        boxShadow: '0 4px 24px rgba(15,23,42,0.06)',
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 800, color: platformColor, letterSpacing: '0.06em', marginBottom: 8 }}>
        SUGGESTED WINDOW
      </div>
      <p style={{ margin: '0 0 10px', fontSize: 18, fontWeight: 800, color: '#0f172a', lineHeight: 1.35 }}>
        Aim for <span style={{ color: platformColor }}>{resolved.bestDay}</span> around{' '}
        <span style={{ color: platformColor }}>{formatHour12(resolved.bestHour)}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#64748b' }}> ({tz})</span>
      </p>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: '#475569', lineHeight: 1.45 }}>
        <strong>{resolved.totalPosts}</strong> post{resolved.totalPosts !== 1 ? 's' : ''} on <strong>{platformLabel}</strong>
        {resolved.inferredFromDetail ? ' (from your post list).' : '.'}
      </p>
      {extras.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>Also strong</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#475569', lineHeight: 1.6 }}>
            {extras.slice(0, 3).map((s, i) => (
              <li key={i}>
                {DAY_FULL_LABELS[s.d]} · {formatHour12(s.h)} <span style={{ color: '#94a3b8' }}>({s.c} post{s.c !== 1 ? 's' : ''})</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function BestTimeEmptyGuide({ platformLabel, platformColor }) {
  return (
    <div
      style={{
        padding: '20px 22px',
        borderRadius: 14,
        background: '#f8fafc',
        border: '1.5px dashed #cbd5e1',
        marginBottom: 8,
      }}
    >
      <p style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#334155' }}>
        No posting history yet for <span style={{ color: platformColor }}>{platformLabel}</span>
      </p>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
        We build your personal “best time” from <strong>when your posts actually go out</strong>. Connect the account,
        publish or schedule a few posts, then return here.
      </p>
      <ol style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: '#475569', lineHeight: 1.65 }}>
        <li>
          <strong>Connected Accounts</strong> — make sure {platformLabel} is linked.
        </li>
        <li>
          <strong>Video Publisher</strong> — publish or schedule content to {platformLabel}.
        </li>
        <li>
          <strong>Refresh</strong> — come back to Trends → Best Time; the heatmap fills as we see more timestamps.
        </li>
      </ol>
    </div>
  );
}

/* ── Heatmap ─────────────────────────────────────────────────── */
function PostHeatmap({ resolved, postsDetail = [], platformLabel = 'this platform' }) {
  const [selected, setSelected] = useState(null);

  const heatmap = resolved?.heatmap;
  const bestDay = resolved?.bestDay;
  const bestHour = resolved?.bestHour;
  const bestDayIndex = resolved?.bestDayIndex ?? -1;

  if (!heatmap || heatmap.length === 0) {
    return <p style={{ color: '#94a3b8', fontSize: 13 }}>No posts yet to analyze.</p>;
  }

  const maxVal = Math.max(...heatmap.flat(), 1);
  const selectedPosts = selected
    ? postsDetail.filter((p) => {
        if (p.dayIndex != null && p.hour != null) {
          return Number(p.dayIndex) === selected.d && Number(p.hour) === selected.h;
        }
        const raw = p.createdAt || p.publishedAt || p.dateTime;
        if (!raw) return false;
        const dt = new Date(raw);
        if (Number.isNaN(dt.getTime())) return false;
        const d = (dt.getDay() + 6) % 7;
        const h = dt.getHours();
        return d === selected.d && h === selected.h;
      })
    : [];

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ overflowX: 'auto', width: '100%', display: 'flex', justifyContent: 'center' }}>
        <div style={{ display: 'grid', gridTemplateColumns: `52px repeat(24, 32px)`, gap: 3, minWidth: 'max-content', margin: '0 auto' }}>
          <div />
          {HOUR_LABELS.map((h, i) => (
            <div key={i} style={{
              fontSize: 9, textAlign: 'center', paddingBottom: 4,
              fontWeight: i === bestHour ? 800 : 400,
              color: i === bestHour ? '#6366f1' : '#94a3b8',
            }}>{h}</div>
          ))}
          {DAY_LABELS.map((day, d) => (
            <React.Fragment key={d}>
              <div style={{
                fontSize: 11, color: '#475569', display: 'flex', alignItems: 'center',
                fontWeight: d === bestDayIndex ? 800 : 500, paddingRight: 8,
              }}>{day}</div>
              {(heatmap[d] || []).map((count, h) => {
                const intensity  = count / maxVal;
                const isSelected = selected?.d === d && selected?.h === h;
                const isBest     = d === bestDayIndex && h === bestHour;
                const bg = count > 0 ? `rgba(99,102,241,${0.12 + intensity * 0.88})` : '#f1f5f9';
                const slotPosts = (postsDetail || []).filter((p) => {
                  if (p.dayIndex != null && p.hour != null) {
                    return Number(p.dayIndex) === d && Number(p.hour) === h;
                  }
                  const raw = p.createdAt || p.publishedAt || p.dateTime;
                  if (!raw) return false;
                  const dt = new Date(raw);
                  if (Number.isNaN(dt.getTime())) return false;
                  const dd = (dt.getDay() + 6) % 7;
                  const hh = dt.getHours();
                  return dd === d && hh === h;
                });
                const slotDateTimes = [...new Set(
                  slotPosts
                    .map((p) => p.createdAt || p.publishedAt || p.dateTime)
                    .filter(Boolean)
                    .map((raw) => {
                      try {
                        return parseUtcDate(raw).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
                      } catch {
                        return String(raw);
                      }
                    })
                )].slice(0, 2);
                const slotPlatforms = [...new Set(
                  slotPosts
                    .map((p) => (p.platform || '').toString().trim())
                    .filter(Boolean)
                )].slice(0, 3);
                const hoverParts = [
                  `${DAY_FULL_LABELS[d]} ${formatHour12(h)} — ${count} post${count !== 1 ? 's' : ''}`,
                  slotDateTimes.length ? `Date/Time: ${slotDateTimes.join(' | ')}` : '',
                  slotPlatforms.length ? `Platforms: ${slotPlatforms.join(', ')}` : '',
                ].filter(Boolean);
                return (
                  <div
                    key={h}
                    onClick={() => count > 0 && setSelected(isSelected ? null : { d, h })}
                    title={hoverParts.join('\n')}
                    style={{
                      width: 32, height: 28, borderRadius: 5,
                      background: isSelected ? '#6366f1' : bg,
                      outline: isBest && !isSelected ? '2px solid #6366f1' : 'none',
                      cursor: count > 0 ? 'pointer' : 'default',
                      transition: 'transform 0.1s',
                      transform: isSelected ? 'scale(1.15)' : 'scale(1)',
                    }}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div style={{ fontSize: 11, color: '#64748b', marginTop: 12, textAlign: 'center', maxWidth: 720 }}>
        Your busiest slot on <strong>{platformLabel}</strong>: <strong>{bestDay}</strong> at{' '}
        <strong>{formatHour12(bestHour)}</strong> (by post count)
      </div>

      {selected && selectedPosts.length === 0 && (
        <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 12, lineHeight: 1.5 }}>
          This cell has activity in the heatmap, but the API did not return individual posts for it. Try another cell or
          refresh after more posts sync.
        </p>
      )}

      {selected && selectedPosts.length > 0 && (
        <div style={{ marginTop: 16, background: '#f8fafc', borderRadius: 14, border: '1.5px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', background: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>
              {DAY_FULL_LABELS[selected.d]} {formatHour12(selected.h)} — {selectedPosts.length} post{selectedPosts.length !== 1 ? 's' : ''}
            </span>
            <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 16 }}>✕</button>
          </div>
          <div style={{ maxHeight: 440, overflowY: 'auto' }}>
          {selectedPosts.map((p, i) => {
            const platColor = PLATFORM_COLORS[p.platform?.toLowerCase()] || '#6366f1';
            const rowPlat = PLATFORMS.find((x) => x.id === (p.platform || '').toLowerCase());
            const date = p.createdAt ? parseUtcDate(p.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
            return (
              <div key={i} style={{ padding: '14px 16px', borderBottom: i < selectedPosts.length - 1 ? '1px solid #f1f5f9' : 'none', background: '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {rowPlat ? <PlatformIcon platform={rowPlat} size={22} /> : null}
                    <span style={{ background: platColor, color: '#fff', borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700, textTransform: 'capitalize' }}>{rowPlat?.label || p.platform || 'Unknown'}</span>
                  </span>
                  <span style={{ background: '#f1f5f9', color: '#64748b', borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>{p.mediaType || 'Post'}</span>
                  <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 'auto' }}>{date}</span>
                </div>
                <p style={{ margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 100, overflowY: 'auto' }}>
                  {p.caption || <em style={{ color: '#94a3b8' }}>No caption</em>}
                </p>
              </div>
            );
          })}
          </div>
        </div>
      )}
    </div>
  );
}

function getTypeCountNorm(normalizedByType, key) {
  const v = normalizedByType[key];
  if (v == null) return 0;
  if (typeof v === 'number') return v;
  return Number(v.count) || 0;
}

/** Map varied API keys (photo, carousel, …) into video | image | text totals. */
function aggregateCanonicalTypeCounts(rawByType) {
  const totals = { video: 0, image: 0, text: 0 };
  const toVideo = new Set(['video', 'videos', 'reel', 'reels', 'short', 'shorts', 'clip', 'clips', 'vod']);
  const toImage = new Set([
    'image', 'images', 'photo', 'photos', 'carousel', 'carousels', 'picture', 'pictures',
    'pin', 'pins', 'igcarousel', 'media',
  ]);
  const toText = new Set(['text', 'texts', 'status', 'link', 'article', 'poll', 'polls', 'caption', 'captions']);

  Object.entries(rawByType || {}).forEach(([k, v]) => {
    const key = String(k).toLowerCase().replace(/[\s_-]+/g, '');
    const n = typeof v === 'number' ? v : Number(v?.count) || 0;
    if (!n) return;
    if (toVideo.has(key)) totals.video += n;
    else if (toImage.has(key)) totals.image += n;
    else if (toText.has(key)) totals.text += n;
    else if (key === 'video' || key.endsWith('video')) totals.video += n;
    else if (/photo|carousel|image|picture|pin/.test(key)) totals.image += n;
    else if (/text|status|link|article|poll/.test(key)) totals.text += n;
  });
  return totals;
}

/** Server can send { video: { youtube: 4 }, image: { instagram: 3 }, text: {...} } — any alias key merged per canonical type. */
function extractExplicitTypePlatform(byTypeByPlatform, typeKey) {
  if (!byTypeByPlatform || typeof byTypeByPlatform !== 'object') return null;
  const raw =
    byTypeByPlatform[typeKey] ||
    byTypeByPlatform[typeKey.toLowerCase()] ||
    byTypeByPlatform[typeKey.charAt(0).toUpperCase() + typeKey.slice(1).toLowerCase()];
  if (!raw || typeof raw !== 'object') return null;
  const segs = [];
  Object.entries(raw).forEach(([k, v]) => {
    const n = typeof v === 'number' ? v : Number(v?.count) || 0;
    if (n <= 0) return;
    const id = String(k).toLowerCase();
    const pc = PLATFORMS.find((p) => p.id === id);
    segs.push({
      platformId: id,
      label: pc?.label || id,
      color: pc?.color || PLATFORM_COLORS[id] || '#64748b',
      count: n,
    });
  });
  return segs.length ? segs : null;
}

const EXPLICIT_ALIASES = {
  video: ['video', 'videos', 'reel', 'reels', 'short', 'shorts', 'clip', 'clips'],
  image: ['image', 'images', 'photo', 'photos', 'carousel', 'picture', 'pictures', 'pin', 'pins', 'igcarousel'],
  text: ['text', 'texts', 'status', 'link', 'article', 'poll', 'polls', 'caption'],
};

/** Merge all API variant keys into one segment list per Video / Image / Text tower. */
function extractExplicitForCanonical(byTypeByPlatform, canonKey) {
  if (!byTypeByPlatform || typeof byTypeByPlatform !== 'object') return null;
  const aliases = EXPLICIT_ALIASES[canonKey] || [canonKey];
  const merged = {};
  for (const tk of aliases) {
    const part = extractExplicitTypePlatform(byTypeByPlatform, tk);
    if (!part) continue;
    part.forEach((s) => {
      merged[s.platformId] = (merged[s.platformId] || 0) + s.count;
    });
  }
  const arr = Object.entries(merged).map(([platformId, count]) => {
    const pc = PLATFORMS.find((p) => p.id === platformId);
    return {
      platformId,
      label: pc?.label || platformId,
      color: pc?.color || PLATFORM_COLORS[platformId] || '#64748b',
      count,
    };
  }).filter((x) => x.count > 0);
  return arr.length ? arr : null;
}

/**
 * Each tower stacks platform brand colors by share of that content type.
 * Prefers postPerf.byTypeByPlatform; else estimates from byPlatform mix (same ratio applied per type).
 */
function TypeTowersChart({ postPerf, platformColor = '#6366f1' }) {
  const canon = aggregateCanonicalTypeCounts(postPerf?.byType || {});
  const normalized = {
    video: { count: canon.video },
    image: { count: canon.image },
    text: { count: canon.text },
  };
  const byPlatNorm = {};
  Object.entries(postPerf?.byPlatform || {}).forEach(([k, v]) => {
    byPlatNorm[String(k).toLowerCase()] = Number(v) || 0;
  });
  const totalPosts = Object.values(byPlatNorm).reduce((a, b) => a + b, 0);

  const explicitRoot =
    postPerf?.byTypeByPlatform ||
    postPerf?.typeByPlatform ||
    postPerf?.postsByTypeAndPlatform;

  const tint = typeTowerColors(platformColor);
  const tintFor = (key) => (key === 'video' ? tint.video : key === 'image' ? tint.image : tint.text);

  function segmentsForType(typeKey) {
    const tc = getTypeCountNorm(normalized, typeKey);
    if (tc <= 0) return { segments: [], total: 0 };

    const ex = extractExplicitForCanonical(explicitRoot, typeKey);
    if (ex) {
      const sum = ex.reduce((s, x) => s + x.count, 0) || 1;
      return {
        segments: ex.map((x) => ({
          ...x,
          pctOfType: Math.round((x.count / sum) * 1000) / 10,
        })),
        total: tc,
      };
    }

    if (totalPosts <= 0) {
      return {
        segments: [{ platformId: '_all', label: 'All', color: tintFor(typeKey), count: tc, pctOfType: 100 }],
        total: tc,
      };
    }

    const segs = [];
    PLATFORMS.forEach((p) => {
      const share = byPlatNorm[p.id] || 0;
      if (share <= 0) return;
      const cnt = tc * (share / totalPosts);
      if (cnt < 0.005) return;
      segs.push({
        platformId: p.id,
        label: p.label,
        color: p.color,
        count: cnt,
        pctOfType: (cnt / tc) * 100,
      });
    });
    if (segs.length === 0) {
      return {
        segments: [{ platformId: '_all', label: 'Mix', color: tintFor(typeKey), count: tc, pctOfType: 100 }],
        total: tc,
      };
    }
    return { segments: segs, total: tc };
  }

  const towers = [
    { key: 'video', label: 'Video' },
    { key: 'image', label: 'Image' },
    { key: 'text', label: 'Text' },
  ];
  const trackBg = brandTowerTrackBg(platformColor);
  const rows = towers.map((t) => {
    const { segments, total } = segmentsForType(t.key);
    return { ...t, segments, count: total };
  });
  const max = Math.max(...rows.map((r) => r.count), 1);
  const sumTypes = rows.reduce((s, r) => s + r.count, 0) || 1;
  const CH = 100;

  return (
    <div>
      <p style={{ fontSize: 11, color: '#94a3b8', margin: '0 0 12px', lineHeight: 1.45, maxWidth: 560 }}>
        <strong>Video</strong>, <strong>Image</strong>, and <strong>Text</strong> towers all work the same: each bar is stacked by{' '}
        <strong>platform</strong> (brand colors). True splits when the API sends{' '}
        <code style={{ fontSize: 10 }}>byTypeByPlatform</code> for each type; otherwise shares are estimated from overall posts per platform.
        Hover a band for % of that tower.
      </p>
      <div style={{ display: 'flex', gap: 28, alignItems: 'flex-end', minHeight: 168, justifyContent: 'center', flexWrap: 'wrap' }}>
        {rows.map((r) => {
          const pctOfAllTypes = Math.round((r.count / sumTypes) * 1000) / 10;
          const innerPx = Math.max((r.count / max) * CH, r.count > 0 ? 14 : 0);
          const borderTint = r.segments[0]?.color || tintFor(r.key);
          return (
            <div key={r.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, width: 82 }}>
              <span style={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>{r.count}</span>
              <div
                title={`${r.label}: ${r.count} posts (${pctOfAllTypes}% of video+image+text)`}
                style={{
                  width: 60,
                  height: 118,
                  background: trackBg,
                  borderRadius: '12px 12px 10px 10px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  padding: 5,
                  boxSizing: 'border-box',
                  border: `1px solid ${borderTint}44`,
                }}
              >
                <div
                  style={{
                    width: '100%',
                    height: innerPx,
                    display: 'flex',
                    flexDirection: 'column-reverse',
                    borderRadius: 10,
                    overflow: 'hidden',
                    boxShadow: `0 2px 10px ${borderTint}33`,
                  }}
                >
                  {r.segments.map((seg) => (
                    <div
                      key={seg.platformId}
                      title={`${seg.label}: ~${Math.round(seg.count)} (${Math.round(seg.pctOfType * 10) / 10}% of ${r.label})`}
                      style={{
                        flex: Math.max(seg.count, 0.001),
                        minHeight: seg.count > 0 ? 3 : 0,
                        background: seg.color,
                        boxSizing: 'border-box',
                        borderTop: r.segments.length > 1 ? '1px solid rgba(255,255,255,0.35)' : 'none',
                      }}
                    />
                  ))}
                </div>
              </div>
              <span style={{ fontSize: 11, color: '#475569', fontWeight: 700, textTransform: 'capitalize' }}>{r.label}</span>
              <span style={{ fontSize: 11, color: tintFor(r.key), fontWeight: 800 }}>{pctOfAllTypes}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const SCHEDULED_YELLOW = '#facc15';
const SCHEDULED_YELLOW_BORDER = '#eab308';
const SCHEDULED_SELECTED_ORANGE = '#f97316';
const SCHEDULED_SELECTED_ORANGE_BORDER = '#ea580c';
/** Day cell outline when the day has scheduled/pending items (My Posts view) — matches main Content Calendar */
const DAY_BORDER_HAS_SCHEDULED = '#fde68a';

/* ── Per-platform best time summary cards ─────────────────────── */
function PlatformBestTimeCards({ bestTime }) {
  // Use byPlatform from API if present, else compute from postsDetail
  const cards = useMemo(() => {
    const DAY_FULL = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

    // From API-provided per-platform stats
    const apiByPlat = bestTime?.byPlatform;
    if (apiByPlat && typeof apiByPlat === 'object' && Object.keys(apiByPlat).length > 0) {
      return Object.entries(apiByPlat)
        .filter(([platId]) => !EXCLUDED_BEST_TIME_PLATFORMS.has(String(platId).toLowerCase()))
        .filter(([, v]) => v && Number(v.postCount) > 0)
        .sort((a, b) => Number(b[1].postCount) - Number(a[1].postCount))
        .map(([platId, v]) => {
          const pc = PLATFORMS.find((p) => p.id === platId);
          return {
            id: platId,
            label: pc?.label || platId,
            color: pc?.color || '#64748b',
            bestDay: v.bestDay || 'Monday',
            bestHour: Number(v.bestHour) || 0,
            postCount: Number(v.postCount),
          };
        });
    }

    // Fallback: compute from postsDetail
    const detail = bestTime?.postsDetail || [];
    if (detail.length === 0) return [];
    const map = {};
    detail.forEach((p) => {
      const platId = (p.platform || 'unknown').toLowerCase();
      if (!map[platId]) map[platId] = { counts: Array.from({length:7}, ()=>Array(24).fill(0)), total: 0 };
      let d = p.dayIndex; let h = p.hour;
      if (d == null || h == null) {
        const raw = p.createdAt || p.dateTime;
        if (!raw) return;
        const dt = new Date(raw);
        if (Number.isNaN(dt.getTime())) return;
        d = (dt.getDay() + 6) % 7; h = dt.getHours();
      }
      d = Number(d); h = Number(h);
      if (d >= 0 && d < 7 && h >= 0 && h < 24) {
        map[platId].counts[d][h] += 1;
        map[platId].total += 1;
      }
    });
    return Object.entries(map)
      .filter(([platId]) => !EXCLUDED_BEST_TIME_PLATFORMS.has(String(platId).toLowerCase()))
      .filter(([, v]) => v.total > 0)
      .sort((a, b) => b[1].total - a[1].total)
      .map(([platId, v]) => {
        let bestD = 0, bestH = 0, best = -1;
        for (let d = 0; d < 7; d++) for (let h = 0; h < 24; h++) if (v.counts[d][h] > best) { best = v.counts[d][h]; bestD = d; bestH = h; }
        const pc = PLATFORMS.find((p) => p.id === platId);
        return { id: platId, label: pc?.label || platId, color: pc?.color || '#64748b', bestDay: DAY_FULL[bestD], bestHour: bestH, postCount: v.total };
      });
  }, [bestTime]);

  if (!cards || cards.length === 0) return null;

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 10 }}>
        Best time by platform
        <span style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8', marginLeft: 8 }}>
          (based on when you posted, not audience activity)
        </span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {cards.map((c) => (
          <div key={c.id} style={{
            background: '#fff',
            border: `1.5px solid ${c.color}44`,
            borderRadius: 12,
            padding: '10px 14px',
            minWidth: 140,
            flex: '1 1 160px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
              {(() => { const pc = PLATFORMS.find(p => p.id === c.id); return pc ? <PlatformIcon platform={pc} size={18} /> : null; })()}
              <span style={{ fontSize: 12, fontWeight: 700, color: c.color }}>{c.label}</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 2 }}>
              {c.bestDay.slice(0, 3)} · {formatHour12(c.bestHour)}
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.postCount} post{c.postCount !== 1 ? 's' : ''} tracked</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Calendar API may omit this; try common shapes / keys. */
function normalizeCalendarBestTimeOverlay(payload) {
  if (!payload || typeof payload !== 'object') return [];
  const raw =
    payload.bestTimeOverlay ||
    payload.best_time_overlay ||
    payload.bestTimeHeatmap ||
    payload.dayHourHeatmap ||
    payload.heatmap;
  return Array.isArray(raw) && raw.length > 0 ? raw : [];
}

/**
 * YYYY-MM-DD in the user's local calendar for grid cells.
 * Handles rows that only have scheduledAt / dateTime (common for jobs) without a plain date field.
 */
function calendarLocalDateKey(rec) {
  if (!rec || typeof rec !== 'object') return null;
  const dOnly = rec.date;
  if (typeof dOnly === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dOnly.trim())) {
    return dOnly.trim();
  }
  const raw =
    rec.dateTime ||
    rec.scheduledAt ||
    rec.scheduledTime ||
    rec.publishAt ||
    rec.publishedAt ||
    rec.createdAt ||
    dOnly;
  if (raw == null || String(raw).trim() === '') return null;
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Platforms removed from product UI (API may still return legacy keys). */
const EXCLUDED_BEST_TIME_PLATFORMS = new Set(['reddit', 'trends', 'google_trends']);

function parseDateTimeLocalInput(s) {
  if (s == null || String(s).trim() === '') return null;
  const t = new Date(s).getTime();
  return Number.isFinite(t) ? t : null;
}

/** Best-effort timestamp for calendar list filtering (ms). */
function recordTimeMsForFilter(rec) {
  if (!rec || typeof rec !== 'object') return null;
  const raw =
    rec.dateTime ||
    rec.scheduledAt ||
    rec.scheduledTime ||
    rec.publishAt ||
    rec.publishedAt ||
    rec.createdAt ||
    null;
  if (raw) {
    const t = new Date(raw).getTime();
    if (Number.isFinite(t)) return t;
  }
  const k = calendarLocalDateKey(rec);
  if (k && /^\d{4}-\d{2}-\d{2}$/.test(k)) {
    const t = new Date(`${k}T12:00:00`).getTime();
    return Number.isFinite(t) ? t : null;
  }
  return null;
}

/**
 * Treat as scheduled (yellow square) even when the API put the row in `posts` instead of `scheduled`.
 */
function isCalendarRowScheduled(row) {
  if (!row || typeof row !== 'object') return false;
  const s = String(row.status ?? '').toUpperCase();
  if (s.includes('SCHEDULED')) return true;
  if (['PENDING', 'QUEUED', 'PROCESSING'].includes(s)) return true;
  if (['SUCCESS', 'PUBLISHED', 'FAILED', 'COMPLETED'].includes(s)) return false;
  const raw =
    row.scheduledAt ||
    row.scheduledTime ||
    row.executeAt ||
    row.runAt ||
    row.dateTime ||
    null;
  if (!raw) return false;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) && t > Date.now();
}

function dayScoresFromHeatmapRows(heatmap) {
  return Array.from({ length: 7 }, (_, d) => {
    const row = heatmap?.[d];
    if (!Array.isArray(row)) return 0;
    return row.reduce((a, b) => a + (Number(b) || 0), 0);
  });
}

/** When API sends no overlay, infer weekday activity from post + scheduled rows (local calendar dates). */
function dayScoresFromPostDates(records) {
  const c = [0, 0, 0, 0, 0, 0, 0];
  (records || []).forEach((p) => {
    const key = calendarLocalDateKey(p);
    if (!key) return;
    const [yy, mm, dd] = key.split('-').map(Number);
    const d = new Date(yy, mm - 1, dd);
    if (Number.isNaN(d.getTime())) return;
    const dow = (d.getDay() + 6) % 7;
    c[dow] += 1;
  });
  return c;
}

function normalizeFallbackPost(p = {}) {
  return {
    id: p.id ?? null,
    platform: String(p.platform || '').toLowerCase(),
    caption: p.caption || '',
    status: p.status || 'SUCCESS',
    mediaType: p.mediaType || p.postType || 'post',
    dateTime: p.createdAt || p.scheduledAt || p.dateTime || null,
    date: (() => {
      const k = calendarLocalDateKey(p);
      return k || null;
    })(),
    createdAt: p.createdAt || null,
    scheduledAt: p.scheduledAt || null,
    mediaUrl: p.mediaUrl || p.videoUrl || p.imageUrl || null,
    thumbnailUrl: p.thumbnailUrl || p.thumbnail || null,
  };
}

function normalizeFallbackScheduled(j = {}) {
  const rawTs =
    j.scheduledAt ||
    j.scheduledTime ||
    j.runAt ||
    j.executeAt ||
    j.dateTime ||
    j.createdAt ||
    null;
  return {
    id: j.id ?? j.jobId ?? null,
    jobId: j.jobId ?? j.id ?? null,
    platform: String(j.platform || '').toLowerCase(),
    caption: j.caption || j.message || '',
    status: j.status || 'SCHEDULED',
    mediaType: j.mediaType || j.postType || 'post',
    dateTime: rawTs,
    date: (() => {
      const k = calendarLocalDateKey({ ...j, dateTime: rawTs, scheduledAt: rawTs });
      return k || null;
    })(),
    scheduledAt: rawTs,
    mediaUrl: j.mediaUrl || j.videoUrl || j.imageUrl || null,
    thumbnailUrl: j.thumbnailUrl || j.thumbnail || null,
  };
}

function extractScheduledRows(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];
  const candidates = [
    payload.items,
    payload.jobs,
    payload.scheduled,
    payload.data,
    payload.results,
    payload.content,
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  return [];
}

const YEARLY_DOW_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

/** Weekday activity for one month (same rules as monthly Best Times). */
function monthDowScoresFromCalendarPayload(d) {
  const hm = normalizeCalendarBestTimeOverlay(d);
  let scores = dayScoresFromHeatmapRows(hm);
  const maxH = Math.max(...scores, 0);
  if (maxH === 0 && d) {
    scores = dayScoresFromPostDates([...(d.posts || []), ...(d.scheduled || [])]);
  }
  const max = Math.max(...scores, 0);
  return { scores, max: Math.max(max, 1), hasAny: max > 0 };
}

/** Per-day and weekday aggregates for yearly calendar cards. */
function aggregateYearMonthStats(year, month1Based, d) {
  const posts = Array.isArray(d?.posts) ? d.posts : [];
  const scheduled = Array.isArray(d?.scheduled) ? d.scheduled : [];
  const all = [...posts, ...scheduled];
  const daysInMonth = new Date(year, month1Based, 0).getDate();
  const dayIntensity = Array(daysInMonth).fill(0);
  const prefix = `${year}-${String(month1Based).padStart(2, '0')}-`;
  all.forEach((rec) => {
    const key = calendarLocalDateKey(rec);
    if (!key || !key.startsWith(prefix)) return;
    const dd = parseInt(key.slice(8, 10), 10);
    if (dd >= 1 && dd <= daysInMonth) dayIntensity[dd - 1] += 1;
  });
  const maxDayIntensity = Math.max(...dayIntensity, 0);
  const { scores: dowScores, max: dowMax, hasAny: dowHasAny } = monthDowScoresFromCalendarPayload(d);
  return {
    posts: posts.length,
    scheduled: scheduled.length,
    total: posts.length + scheduled.length,
    dayIntensity,
    maxDayIntensity: Math.max(maxDayIntensity, 1),
    dowScores,
    dowMax,
    dowHasAny,
  };
}

/* ── Trends Calendar ─────────────────────────────────────────── */
function TrendsCalendar({ authHeaders }) {
  const today     = new Date();
  const [year,  setYear]  = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-based
  const [data,  setData]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [calView, setCalView] = useState('posts'); // 'posts' | 'besttime'
  const [selectedDay, setSelectedDay] = useState(null);
  const [hidePastPosts, setHidePastPosts] = useState(false);
  const [calTip, setCalTip] = useState(null); // hover on mini markers
  const [rangeView, setRangeView] = useState('monthly'); // 'monthly' | 'yearly'
  const [yearSummary, setYearSummary] = useState({});
  const [yearSummaryLoading, setYearSummaryLoading] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [pastFrom, setPastFrom] = useState('');
  const [pastUntil, setPastUntil] = useState('');
  const [schedFrom, setSchedFrom] = useState('');
  const [schedUntil, setSchedUntil] = useState('');

  const loadCalendar = useCallback(() => {
    setLoading(true);
    const headers = authHeaders();
    fetch(`${API}/api/analytics/calendar?year=${year}&month=${month}`, { headers })
      .then(async (r) => {
        if (!r.ok) throw new Error(`calendar ${r.status}`);
        return r.json();
      })
      .then((d) => { setData(d); setLoading(false); })
      .catch(async () => {
        // Fallback: rebuild month view from overview + history when calendar API is unavailable.
        try {
          const [overviewRes, historyRes, scheduleRes] = await Promise.all([
            fetch(`${API}/api/analytics/overview`, { headers }),
            fetch(`${API}/api/social/post/history?limit=400`, { headers }),
            fetch(`${API}/api/video-content/schedule`, { headers }),
          ]);
          const overview = overviewRes.ok ? await overviewRes.json() : {};
          const history = historyRes.ok ? await historyRes.json() : [];
          const schedulePayload = scheduleRes.ok ? await scheduleRes.json() : [];
          const recent = Array.isArray(overview?.recentActivity) ? overview.recentActivity : [];
          const VALID_PLATFORM_IDS = PLATFORMS.map(p => p.id);
          const mergedPosts = [...recent, ...(Array.isArray(history) ? history : [])]
            .map(normalizeFallbackPost)
            .filter((p) => VALID_PLATFORM_IDS.includes((p.platform || '').toLowerCase()))
            .filter((p) => {
              const key = calendarLocalDateKey(p);
              if (!key) return false;
              const [yy, mm] = key.split('-').map(Number);
              return yy === year && mm === month;
            });
          const mergedScheduled = extractScheduledRows(schedulePayload)
            .map(normalizeFallbackScheduled)
            .filter((j) => {
              const status = String(j.status || '').toUpperCase();
              // Keep only future/pending style jobs in "scheduled" bucket.
              return status === '' || status === 'SCHEDULED' || status === 'PENDING' || status === 'QUEUED';
            })
            .filter((j) => {
              const key = calendarLocalDateKey(j);
              if (!key) return false;
              const [yy, mm] = key.split('-').map(Number);
              return yy === year && mm === month;
            });
          setData({
            posts: mergedPosts,
            scheduled: mergedScheduled,
            bestTimeOverlay: [],
            _fallback: true,
          });
        } catch {
          setData({ posts: [], scheduled: [], bestTimeOverlay: [], _fallback: true });
        } finally {
          setLoading(false);
        }
      });
  }, [year, month, authHeaders]);

  useEffect(() => { loadCalendar(); }, [loadCalendar]);

  useEffect(() => {
    setPastFrom('');
    setPastUntil('');
    setSchedFrom('');
    setSchedUntil('');
  }, [year, month]);

  const loadYearSummary = useCallback(async () => {
    setYearSummaryLoading(true);
    const headers = authHeaders();
    try {
      const monthNums = Array.from({ length: 12 }, (_, i) => i + 1);
      const results = await Promise.all(
        monthNums.map(async (m) => {
          try {
            const r = await fetch(`${API}/api/analytics/calendar?year=${year}&month=${m}`, { headers });
            if (!r.ok) return [m, aggregateYearMonthStats(year, m, {})];
            const d = await r.json();
            return [m, aggregateYearMonthStats(year, m, d)];
          } catch {
            return [m, aggregateYearMonthStats(year, m, {})];
          }
        })
      );
      setYearSummary(Object.fromEntries(results));
    } finally {
      setYearSummaryLoading(false);
    }
  }, [authHeaders, year]);

  useEffect(() => {
    if (rangeView === 'yearly') loadYearSummary();
  }, [rangeView, loadYearSummary]);

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
    setSelectedDay(null);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
    setSelectedDay(null);
  };

  const prevRange = () => {
    if (rangeView === 'yearly') {
      setYear((y) => y - 1);
      setSelectedDay(null);
    } else {
      prevMonth();
    }
  };
  const nextRange = () => {
    if (rangeView === 'yearly') {
      setYear((y) => y + 1);
      setSelectedDay(null);
    } else {
      nextMonth();
    }
  };

  const yearTotals = useMemo(() => {
    let pub = 0;
    let sch = 0;
    for (let m = 1; m <= 12; m += 1) {
      const s = yearSummary[m];
      if (s) {
        pub += s.posts || 0;
        sch += s.scheduled || 0;
      }
    }
    return { pub, sch, total: pub + sch };
  }, [yearSummary]);

  // Build calendar grid
  const firstDayOfMonth = new Date(year, month - 1, 1).getDay(); // 0=Sun
  const daysInMonth     = new Date(year, month, 0).getDate();
  // Shift so Mon=0
  const offset = (firstDayOfMonth + 6) % 7;

  // Group posts + scheduled by local calendar key YYYY-MM-DD (not only p.date — jobs often use scheduledAt)
  const byDate = {};
  if (data) {
    const seenJobIds = new Set();
    (data.scheduled || []).forEach((j) => {
      const key = calendarLocalDateKey(j);
      if (!key) return;
      const jid = j.jobId ?? j.id;
      if (jid != null) seenJobIds.add(String(jid));
      if (!byDate[key]) byDate[key] = [];
      byDate[key].push({ ...j, _kind: 'scheduled' });
    });
    (data.posts || []).forEach((p) => {
      const pid = p.jobId ?? p.id;
      if (pid != null && seenJobIds.has(String(pid))) return;
      const key = calendarLocalDateKey(p);
      if (!key) return;
      const kind = isCalendarRowScheduled(p) ? 'scheduled' : 'post';
      if (!byDate[key]) byDate[key] = [];
      byDate[key].push({ ...p, _kind: kind });
    });
  }

  const bestTimeCal = useMemo(() => {
    const hm = normalizeCalendarBestTimeOverlay(data);
    let scores = dayScoresFromHeatmapRows(hm);
    let max = Math.max(...scores, 0);
    let source = 'heatmap';
    if (max === 0 && data) {
      scores = dayScoresFromPostDates([...(data.posts || []), ...(data.scheduled || [])]);
      max = Math.max(...scores, 0);
      source = max > 0 ? 'posts' : 'none';
    } else if (max > 0) {
      source = 'heatmap';
    } else {
      source = 'none';
    }
    return {
      dayScores: scores,
      maxDayScore: Math.max(max, 1),
      hasAny: max > 0,
      source,
    };
  }, [data]);

  /**
   * Same buckets as the calendar grid: API often puts future items only in `posts`
   * (status SCHEDULED / future scheduledAt) while `scheduled` is empty — listing only
   * `data.scheduled` showed "(0)" even when the month had many scheduled chips.
   */
  const upcomingScheduledForList = useMemo(() => {
    if (!data) return [];
    const seenJobIds = new Set();
    const rows = [];
    const inThisMonth = (rec) => {
      const key = calendarLocalDateKey(rec);
      if (!key) return false;
      const [yy, mm] = key.split('-').map(Number);
      return yy === year && mm === month;
    };
    (data.scheduled || []).forEach((j) => {
      if (!inThisMonth(j)) return;
      const jid = j.jobId ?? j.id;
      if (jid != null) seenJobIds.add(String(jid));
      rows.push(j);
    });
    (data.posts || []).forEach((p) => {
      const pid = p.jobId ?? p.id;
      if (pid != null && seenJobIds.has(String(pid))) return;
      if (!isCalendarRowScheduled(p)) return;
      if (!inThisMonth(p)) return;
      if (pid != null) seenJobIds.add(String(pid));
      rows.push(p);
    });
    return rows;
  }, [data, year, month]);

  const pastPostsForList = useMemo(() => {
    if (!data) return [];
    let posts = [...(data.posts || [])];
    const fromMs = parseDateTimeLocalInput(pastFrom);
    const untilMs = parseDateTimeLocalInput(pastUntil);
    if (fromMs != null || untilMs != null) {
      posts = posts.filter((p) => {
        const t = recordTimeMsForFilter(p);
        if (t == null) return false;
        if (fromMs != null && t < fromMs) return false;
        if (untilMs != null && t > untilMs) return false;
        return true;
      });
    }
    return posts;
  }, [data, pastFrom, pastUntil]);

  const upcomingScheduledFiltered = useMemo(() => {
    let rows = [...upcomingScheduledForList];
    const fromMs = parseDateTimeLocalInput(schedFrom);
    const untilMs = parseDateTimeLocalInput(schedUntil);
    if (fromMs != null || untilMs != null) {
      rows = rows.filter((j) => {
        const t = recordTimeMsForFilter(j);
        if (t == null) return false;
        if (fromMs != null && t < fromMs) return false;
        if (untilMs != null && t > untilMs) return false;
        return true;
      });
    }
    return rows;
  }, [upcomingScheduledForList, schedFrom, schedUntil]);

  // Find selected day's items
  const selKey = selectedDay ? `${year}-${String(month).padStart(2,'0')}-${String(selectedDay).padStart(2,'0')}` : null;
  const selItems = selKey ? (byDate[selKey] || []) : [];

  return (
    <div>
      {/* Sub-view toggle + New Post button */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        {[['posts','📌 My Posts'],['besttime','🕐 Best Times']].map(([id, label]) => (
          <button key={id} onClick={() => setCalView(id)} style={{
            padding: '7px 18px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            background: calView === id ? '#6366f1' : 'transparent',
            color: calView === id ? '#fff' : '#64748b',
            border: `1.5px solid ${calView === id ? '#6366f1' : '#e2e8f0'}`,
          }}>{label}</button>
        ))}
        <button onClick={() => setComposeOpen(true)} style={{
          marginLeft: 'auto', padding: '7px 16px', borderRadius: 20, fontSize: 12,
          fontWeight: 700, cursor: 'pointer', background: '#6366f1', color: '#fff', border: 'none',
        }}>+ New Post</button>
      </div>

      {calView === 'besttime' && (
        <div
          style={{
            marginBottom: 14,
            padding: '10px 14px',
            borderRadius: 10,
            background: '#fffbeb',
            border: '1px solid #fde68a',
            fontSize: 12,
            color: '#92400e',
            lineHeight: 1.5,
          }}
        >
          <strong>Best Times</strong> shows weekday activity shading only — it does{' '}
          <strong>not</strong> list scheduled posts. Tap <strong>📌 My Posts</strong> above to see yellow squares
          (scheduled) and colored dots (published) on each day. In <strong>Yearly</strong> view, each month card shows a
          small weekday bar chart for that month.
        </div>
      )}

      {/* Range toggle + month nav */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setRangeView('monthly')}
            style={{
              padding: '6px 12px',
              borderRadius: 18,
              border: `1.5px solid ${rangeView === 'monthly' ? '#6366f1' : '#e2e8f0'}`,
              background: rangeView === 'monthly' ? '#6366f1' : '#fff',
              color: rangeView === 'monthly' ? '#fff' : '#64748b',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >Monthly</button>
          <button
            onClick={() => setRangeView('yearly')}
            style={{
              padding: '6px 12px',
              borderRadius: 18,
              border: `1.5px solid ${rangeView === 'yearly' ? '#6366f1' : '#e2e8f0'}`,
              background: rangeView === 'yearly' ? '#6366f1' : '#fff',
              color: rangeView === 'yearly' ? '#fff' : '#64748b',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >Yearly</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button type="button" onClick={prevRange} style={s.navBtn} aria-label={rangeView === 'yearly' ? 'Previous year' : 'Previous month'}>‹</button>
          <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', minWidth: 130, textAlign: 'center' }}>
            {rangeView === 'monthly' ? `${MONTH_NAMES[month - 1]} ${year}` : year}
          </span>
          <button type="button" onClick={nextRange} style={s.navBtn} aria-label={rangeView === 'yearly' ? 'Next year' : 'Next month'}>›</button>
        </div>
      </div>

      {rangeView === 'yearly' ? (
        <div style={{
          background: '#fff',
          border: '1.5px solid #e2e8f0',
          borderRadius: 14,
          padding: 14,
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 12,
        }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={{
              padding: '12px 14px',
              background: '#f8fafc',
              borderRadius: 12,
              border: '1px solid #e2e8f0',
              marginBottom: 4,
            }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: '#1e293b' }}>{year} at a glance</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 6, lineHeight: 1.5 }}>
                {yearSummaryLoading ? (
                  'Loading all months…'
                ) : (
                  <>
                    <strong style={{ color: '#15803d' }}>{yearTotals.pub}</strong> published
                    {' · '}
                    <strong style={{ color: '#a16207' }}>{yearTotals.sch}</strong> scheduled
                    {yearTotals.total === 0 && (
                      <span> — activity appears here once you publish or schedule from Video Publisher.</span>
                    )}
                  </>
                )}
              </div>
              {calView === 'besttime' && (
                <div style={{ fontSize: 11, color: '#57534e', marginTop: 8, lineHeight: 1.45 }}>
                  <strong>Best Times (yearly):</strong> each card shows <strong>weekday volume</strong> for that month (bar height = relative posts). Open a month for the full heatmap.
                </div>
              )}
              {calView === 'posts' && (
                <div style={{ fontSize: 11, color: '#57534e', marginTop: 8, lineHeight: 1.45 }}>
                  <strong>My Posts (yearly):</strong> each row of blocks is <strong>day 1 → last day</strong> of the month; darker indigo = more posts that day. Bars below are published vs scheduled share.
                </div>
              )}
            </div>
          </div>
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
            const raw = yearSummary[m];
            const stats = raw && Array.isArray(raw.dayIntensity) ? raw : aggregateYearMonthStats(year, m, {});
            return (
              <button
                key={m}
                type="button"
                onClick={() => { setMonth(m); setRangeView('monthly'); setSelectedDay(null); }}
                style={{
                  border: `1.5px solid ${m === month ? '#6366f1' : '#e2e8f0'}`,
                  background: m === month ? '#f5f3ff' : '#fff',
                  borderRadius: 12,
                  padding: '10px 10px 8px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 120,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: '#1e293b' }}>{MONTH_NAMES[m - 1]}</span>
                  {stats.total > 0 && (
                    <span style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: '#4338ca',
                      background: '#eef2ff',
                      padding: '2px 7px',
                      borderRadius: 99,
                    }}>
                      {stats.total}
                    </span>
                  )}
                </div>
                {yearSummaryLoading ? (
                  <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 8, minHeight: 48 }} />
                ) : calView === 'posts' ? (
                  <>
                    <div style={{ display: 'flex', gap: 1, height: 24, alignItems: 'flex-end', marginBottom: 8 }}>
                      {stats.dayIntensity.map((cnt, di) => {
                        const hPct = stats.maxDayIntensity > 0 && cnt > 0
                          ? Math.max(15, (cnt / stats.maxDayIntensity) * 100)
                          : cnt > 0 ? 40 : 8;
                        const alpha = stats.maxDayIntensity > 0 && cnt > 0
                          ? 0.28 + (cnt / stats.maxDayIntensity) * 0.72
                          : 0.14;
                        return (
                          <div
                            key={di}
                            title={`${MONTH_NAMES[m - 1]} ${di + 1}: ${cnt} ${cnt === 1 ? 'item' : 'items'}`}
                            style={{
                              flex: 1,
                              minWidth: 0,
                              height: `${hPct}%`,
                              minHeight: cnt > 0 ? 3 : 2,
                              background: cnt > 0 ? `rgba(99,102,241,${alpha})` : '#f1f5f9',
                              borderRadius: 1,
                            }}
                          />
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', height: 5, borderRadius: 3, overflow: 'hidden', marginBottom: 6, background: '#f1f5f9' }}>
                      {stats.total > 0 ? (
                        <>
                          <div style={{ width: `${(stats.posts / stats.total) * 100}%`, background: '#10b981' }} title="Published share" />
                          <div style={{ width: `${(stats.scheduled / stats.total) * 100}%`, background: '#ca8a04' }} title="Scheduled share" />
                        </>
                      ) : null}
                    </div>
                    <div style={{ fontSize: 10, color: '#64748b', display: 'flex', gap: 10, marginTop: 'auto' }}>
                      <span>Pub <strong style={{ color: '#334155' }}>{stats.posts}</strong></span>
                      <span>Sched <strong style={{ color: '#a16207' }}>{stats.scheduled}</strong></span>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 40, marginBottom: 4 }}>
                      {stats.dowScores.map((sc, di) => {
                        const barPx = stats.dowMax > 0 && sc > 0
                          ? 4 + Math.round((sc / stats.dowMax) * 30)
                          : 3;
                        return (
                          <div
                            key={di}
                            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, minWidth: 0 }}
                            title={`${['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][di]}: ${sc}`}
                          >
                            <div style={{
                              width: '100%',
                              height: barPx,
                              minHeight: 3,
                              background: sc > 0 ? 'linear-gradient(180deg,#4f46e5,#818cf8)' : '#f1f5f9',
                              borderRadius: 2,
                            }} />
                            <span style={{ fontSize: 8, color: '#94a3b8', fontWeight: 700 }}>{YEARLY_DOW_LABELS[di]}</span>
                          </div>
                        );
                      })}
                    </div>
                    {!stats.dowHasAny && (
                      <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4 }}>No weekday data this month</div>
                    )}
                    <div style={{ fontSize: 10, color: '#64748b', marginTop: 'auto' }}>
                      Pub <strong style={{ color: '#334155' }}>{stats.posts}</strong>
                      {' · '}
                      Sched <strong style={{ color: '#a16207' }}>{stats.scheduled}</strong>
                    </div>
                  </>
                )}
              </button>
            );
          })}
        </div>
      ) : loading ? (
        <div style={s.loadingRow}><div style={s.spinner} /> Loading…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 20, alignItems: 'start' }}>
          {/* Calendar grid */}
          <div>
            {/* Day-of-week headers */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 3, marginBottom: 4 }}>
              {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
                <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: '#94a3b8', padding: '4px 0' }}>{d}</div>
              ))}
            </div>
            {/* Cells */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 3 }}>
              {/* Empty offset cells */}
              {Array.from({ length: offset }).map((_, i) => (
                <div key={`e-${i}`} style={{ minHeight: 60, background: '#f8fafc', borderRadius: 8 }} />
              ))}
              {/* Day cells */}
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                const dateKey = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                const items   = byDate[dateKey] || [];
                const posts   = items.filter(x => x._kind === 'post');
                const sched   = items.filter(x => x._kind === 'scheduled');
                const isToday = day === today.getDate() && month === today.getMonth() + 1 && year === today.getFullYear();
                const isSel   = day === selectedDay;
                const hasScheduledDay = calView === 'posts' && sched.length > 0;

                // Best time: shade by weekday score (API heatmap or inferred from post dates)
                const dow = (new Date(year, month - 1, day).getDay() + 6) % 7; // Mon=0
                const score = calView === 'besttime'
                  ? bestTimeCal.dayScores[dow] / bestTimeCal.maxDayScore
                  : 0;
                const maxS = Math.max(...bestTimeCal.dayScores, 0);
                const isPeak =
                  calView === 'besttime' && maxS > 0 && bestTimeCal.dayScores[dow] === maxS;
                const isGood =
                  calView === 'besttime' &&
                  maxS > 0 &&
                  !isPeak &&
                  bestTimeCal.dayScores[dow] >= maxS * 0.5;

                return (
                  <div
                    key={day}
                    onClick={() => setSelectedDay(isSel ? null : day)}
                    title={
                      calView === 'posts'
                        ? `${['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][dow]} · ${posts.length} published, ${sched.length} scheduled`
                        : calView === 'besttime' && bestTimeCal.hasAny
                          ? `${['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][dow]} · relative activity ${Math.round(score * 100)}%`
                          : undefined
                    }
                    style={{
                      minHeight: 60, borderRadius: 8, padding: '6px 6px 4px',
                      cursor: 'pointer',
                      border: `1.5px solid ${
                        isSel
                          ? '#6366f1'
                          : hasScheduledDay
                            ? DAY_BORDER_HAS_SCHEDULED
                            : isToday
                              ? '#818cf8'
                              : '#e2e8f0'
                      }`,
                      background: calView === 'besttime'
                        ? `rgba(99,102,241,${0.1 + score * 0.55})`
                        : isSel ? '#ede9fe' : isToday ? '#f5f3ff' : hasScheduledDay ? '#fffef8' : '#fff',
                      transition: 'background 0.15s, border-color 0.15s',
                    }}
                  >
                    <div style={{
                      fontSize: 11, fontWeight: isToday ? 800 : 600,
                      color: isToday || isSel ? '#6366f1' : '#475569',
                      marginBottom: 4,
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4, flexWrap: 'wrap',
                    }}>
                      <span>{day}</span>
                      {calView === 'posts' && isToday && (
                        <span style={{ fontSize: 8, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Today</span>
                      )}
                    </div>
                    {calView === 'posts' && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {posts.slice(0, 4).map((p, i) => {
                          const pcDot = PLATFORMS.find((x) => x.id === (p.platform || '').toLowerCase());
                          const media = (p.mediaType || p.postType || 'post').toString();
                          return (
                            <span
                              key={i}
                              role="presentation"
                              onMouseEnter={(e) => {
                                const r = e.currentTarget.getBoundingClientRect();
                                setCalTip({
                                  kind: 'post',
                                  item: p,
                                  pc: pcDot,
                                  media,
                                  left: r.left + r.width / 2,
                                  top: r.top,
                                });
                              }}
                              onMouseLeave={() => setCalTip(null)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                lineHeight: 0,
                                cursor: 'default',
                              }}
                            >
                              {pcDot
                                ? <PlatformIcon platform={pcDot} size={12} />
                                : <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#10b981' }} />}
                            </span>
                          );
                        })}
                        {sched.slice(0, 3).map((j, i) => {
                          const pcDot = PLATFORMS.find((x) => x.id === (j.platform || '').toLowerCase());
                          const media = (j.mediaType || j.postType || 'post').toString();
                          return (
                            <span
                              key={`s${i}`}
                              role="presentation"
                              onMouseEnter={(e) => {
                                const r = e.currentTarget.getBoundingClientRect();
                                setCalTip({
                                  kind: 'scheduled',
                                  item: j,
                                  pc: pcDot,
                                  media,
                                  left: r.left + r.width / 2,
                                  top: r.top,
                                });
                              }}
                              onMouseLeave={() => setCalTip(null)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                lineHeight: 0,
                                width: 14,
                                height: 14,
                                borderRadius: 3,
                                // Amber/orange backdrop preserves the "scheduled" visual semantic
                                // from the legend so users can still tell a scheduled logo from a
                                // published one at a glance.
                                background: isSel ? SCHEDULED_SELECTED_ORANGE : SCHEDULED_YELLOW,
                                border: `1px solid ${isSel ? SCHEDULED_SELECTED_ORANGE_BORDER : SCHEDULED_YELLOW_BORDER}`,
                                cursor: 'default',
                              }}
                            >
                              {pcDot && <PlatformIcon platform={pcDot} size={10} />}
                            </span>
                          );
                        })}
                        {(posts.length + sched.length) > 7 && (
                          <div style={{ fontSize: 9, color: '#64748b', lineHeight: '8px', fontWeight: 700 }}>
                            {posts.length + sched.length - 7}
                          </div>
                        )}
                      </div>
                    )}
                    {calView === 'besttime' && isPeak && (
                      <div style={{ fontSize: 9, color: '#4338ca', fontWeight: 800 }}>🔥 Peak</div>
                    )}
                    {calView === 'besttime' && isGood && (
                      <div style={{ fontSize: 9, color: '#6366f1', fontWeight: 700 }}>✓ Good</div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            {calView === 'posts' && (
              <div style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#64748b' }}>
                    <span style={{ display: 'inline-flex', lineHeight: 0 }}>
                      <PlatformIcon platform={PLATFORMS.find(p => p.id === 'youtube') || PLATFORMS[0]} size={12} />
                    </span>
                    Published — <strong>platform logo</strong> (one per post)
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#64748b' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 14, height: 14, borderRadius: 3, background: SCHEDULED_YELLOW, border: `1px solid ${SCHEDULED_YELLOW_BORDER}` }}>
                      <PlatformIcon platform={PLATFORMS.find(p => p.id === 'youtube') || PLATFORMS[0]} size={10} />
                    </span>
                    Scheduled / pending — <strong>logo on amber square</strong> (so you can tell them apart from published)
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#64748b' }}>
                    <div style={{ width: 14, height: 10, borderRadius: 4, border: `1.5px solid ${DAY_BORDER_HAS_SCHEDULED}`, background: '#fffef8' }} />
                    Day has a schedule — pale yellow cell outline
                  </div>
                </div>
                <p style={{ margin: '10px 0 0', fontSize: 11, color: '#94a3b8', lineHeight: 1.5, maxWidth: 640 }}>
                  Squares are <strong>amber/yellow</strong> when the day is not selected; when you <strong>select</strong> that day, squares turn <strong>orange</strong> so they stay visible on the purple highlight.
                </p>
                <p style={{ margin: '10px 0 0', fontSize: 11, color: '#64748b', lineHeight: 1.55, maxWidth: 720 }}>
                  <strong>Today vs a future day:</strong> the marker shape does <strong>not</strong> mean “today” vs “later.”{' '}
                  <strong>Round dots</strong> mean the API returned <strong>published</strong> posts for that date; <strong>squares</strong> mean{' '}
                  <strong>scheduled / not yet published</strong> jobs. So the 3rd can show only dots if your data has publishes that day, while the 10th shows a square if that day only has a schedule—both are normal.
                </p>
              </div>
            )}
            {calView === 'besttime' && (
              <div style={{ marginTop: 12 }}>
                {!loading && !bestTimeCal.hasAny && (
                  <div
                    style={{
                      background: '#f5f3ff',
                      border: '1px solid #c7d2fe',
                      borderRadius: 10,
                      padding: '12px 14px',
                      fontSize: 12,
                      color: '#4338ca',
                      lineHeight: 1.5,
                      marginBottom: 10,
                    }}
                  >
                    <strong>No shading yet.</strong> The calendar response had no <code style={{ fontSize: 10 }}>bestTimeOverlay</code>{' '}
                    (or it was all zeros), and we found no post or scheduled dates in this month&apos;s payload to infer
                    weekdays from. For the full <strong>hour-by-hour</strong> “best time” view, open <strong>2 · Best Time</strong>{' '}
                    in Trends (above). Use <strong>My Posts</strong> here to see scheduled and published markers on each day.
                  </div>
                )}
                {bestTimeCal.source === 'posts' && bestTimeCal.hasAny && (
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8, lineHeight: 1.45 }}>
                    Shading is <strong>estimated</strong> from how many posts fall on each weekday in this calendar data (API did not send a heatmap).
                  </div>
                )}
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 11, color: '#64748b' }}>
                  <span>Darker purple = busier weekday (Mon–Sun pattern, same for each date in that column)</span>
                  <span>🔥 Peak = your strongest weekday · ✓ Good = at least half of peak</span>
                </div>
              </div>
            )}
          </div>

          {/* Day detail panel */}
          {selectedDay && (
            <div style={{ width: 280, background: '#f8fafc', borderRadius: 14, border: '1.5px solid #e2e8f0', padding: 16, maxHeight: 500, overflowY: 'auto' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 12 }}>
                {MONTH_NAMES[month - 1]} {selectedDay}, {year}
              </div>
              {selItems.length === 0 ? (
                <p style={{ fontSize: 12, color: '#94a3b8' }}>No posts on this day.</p>
              ) : selItems.map((item, i) => {
                const rowPc = PLATFORMS.find((x) => x.id === (item.platform || '').toLowerCase());
                const rowMedia = (item.mediaType || item.postType || 'post').toString();
                return (
                <div key={i} style={{
                  background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0',
                  padding: '10px 12px', marginBottom: 8,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {rowPc ? <PlatformIcon platform={rowPc} size={22} /> : null}
                      <span style={{
                        background: PLATFORM_COLORS[item.platform] || '#6366f1',
                        color: '#fff', borderRadius: 20, padding: '3px 10px',
                        fontSize: 10, fontWeight: 700, textTransform: 'capitalize',
                      }}>{rowPc?.label || item.platform}</span>
                    </span>
                    <span style={{
                      background: '#f1f5f9',
                      color: '#475569',
                      borderRadius: 20, padding: '3px 10px', fontSize: 10, fontWeight: 700,
                      textTransform: 'capitalize',
                    }}>{rowMedia}</span>
                    <span style={{
                      background: item._kind === 'scheduled' ? '#fff7ed' : '#f0fdf4',
                      color: item._kind === 'scheduled' ? '#c2410c' : '#16a34a',
                      borderRadius: 20, padding: '3px 10px', fontSize: 10, fontWeight: 600,
                    }}>
                      {item._kind === 'scheduled' ? `⏰ ${item.status}` : `✓ ${item.status}`}
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: 12, color: '#475569', lineHeight: 1.5,
                               overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3,
                               WebkitBoxOrient: 'vertical' }}>
                    {item.caption || <em style={{ color: '#94a3b8' }}>No caption</em>}
                  </p>
                  {item.dateTime && (
                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
                      {parseUtcDate(item.dateTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </div>
                  )}
                </div>
              );
              })}
            </div>
          )}
        </div>
      )}
      {/* Past Posts + Upcoming Posts lists */}
      {rangeView === 'monthly' && !loading && data && (
        <>
      {/* ── Growth Insights (above Past Posts) ── */}
      {data.bestTimeOverlay && (() => {
        const heatmap = data.bestTimeOverlay; // 7×24 array [dayOfWeek][hour]
        const DAY_NAMES = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
        const HOUR_LABELS = Array.from({length:24},(_,h) => {
          const ampm = h < 12 ? 'AM' : 'PM';
          const hr = h === 0 ? 12 : h > 12 ? h - 12 : h;
          return `${hr}${ampm}`;
        });
        const dayTotals = heatmap.map(row => row.reduce((s,v) => s + v, 0));
        const bestDayIdx = dayTotals.indexOf(Math.max(...dayTotals));
        const bestDayCount = dayTotals[bestDayIdx];
        const hourTotals = Array.from({length:24}, (_,h) => heatmap.reduce((s,row) => s + (row[h]||0), 0));
        const bestHourIdx = hourTotals.indexOf(Math.max(...hourTotals));
        let bestSlotVal = 0, bestSlotDay = 0, bestSlotHour = 9;
        heatmap.forEach((row, d) => row.forEach((v, h) => { if (v > bestSlotVal) { bestSlotVal = v; bestSlotDay = d; bestSlotHour = h; } }));
        const totalPosts = dayTotals.reduce((s,v) => s+v, 0);
        if (totalPosts === 0) return null;
        const insights = [
          { icon: '🔥', label: 'Most Active Day', value: DAY_NAMES[bestDayIdx], sub: `${bestDayCount} post${bestDayCount !== 1 ? 's' : ''}`, color: '#f59e0b' },
          { icon: '⏰', label: 'Peak Hour', value: HOUR_LABELS[bestHourIdx], sub: `${hourTotals[bestHourIdx]} post${hourTotals[bestHourIdx] !== 1 ? 's' : ''}`, color: '#6366f1' },
          { icon: '🎯', label: 'Best Posting Slot', value: `${DAY_NAMES[bestSlotDay]} ${HOUR_LABELS[bestSlotHour]}`, sub: `Highest engagement window`, color: '#10b981' },
          { icon: '📊', label: 'Total This Period', value: `${totalPosts}`, sub: `posts published`, color: '#3b82f6' },
        ];
        return (
          <div style={{ marginTop: 24, background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '16px 20px' }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
              💡 Growth Insights
              <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>based on your posting history</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
              {insights.map((ins, i) => (
                <div key={i} style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 14px', borderLeft: `3px solid ${ins.color}` }}>
                  <div style={{ fontSize: 18, marginBottom: 4 }}>{ins.icon}</div>
                  <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>{ins.label}</div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#1e293b' }}>{ins.value}</div>
                  <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{ins.sub}</div>
                </div>
              ))}
            </div>
            {bestSlotVal > 0 && (
              <div style={{ marginTop: 12, padding: '10px 14px', background: '#f0fdf4', borderRadius: 10, fontSize: 12, color: '#166534', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16 }}>💚</span>
                <span><strong>High Engagement Prediction:</strong> Try posting on <strong>{DAY_NAMES[bestSlotDay]}s at {HOUR_LABELS[bestSlotHour]}</strong> — that's your historically strongest slot with {bestSlotVal} post{bestSlotVal !== 1 ? 's' : ''} at peak.</span>
              </div>
            )}
          </div>
        );
      })()}
        <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {/* Past Posts */}
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: '#10b981' }}>✓</span> Past Posts{' '}
              <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>({pastPostsForList.length}{(pastFrom || pastUntil) && (data.posts || []).length !== pastPostsForList.length ? ` of ${(data.posts || []).length}` : ''})</span>
              {(data.posts || []).length > 0 && (
                <button onClick={() => setHidePastPosts(h => !h)} style={{
                  marginLeft: 'auto', fontSize: 11, fontWeight: 600,
                  color: '#94a3b8', background: 'none', border: '1px solid #e2e8f0',
                  borderRadius: 6, padding: '2px 8px', cursor: 'pointer',
                }}>
                  {hidePastPosts ? 'Show' : 'Clear'}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: '#64748b' }}>From</span>
              <input
                type="datetime-local"
                value={pastFrom}
                onChange={(e) => setPastFrom(e.target.value)}
                style={{ fontSize: 11, padding: '4px 8px', borderRadius: 8, border: '1px solid #e2e8f0', color: '#334155' }}
              />
              <span style={{ fontSize: 10, fontWeight: 600, color: '#64748b' }}>Until</span>
              <input
                type="datetime-local"
                value={pastUntil}
                onChange={(e) => setPastUntil(e.target.value)}
                style={{ fontSize: 11, padding: '4px 8px', borderRadius: 8, border: '1px solid #e2e8f0', color: '#334155' }}
              />
            </div>
            {(data.posts || []).length === 0 ? (
              <p style={{ fontSize: 12, color: '#94a3b8' }}>No published posts this month.</p>
            ) : hidePastPosts ? (
              <p style={{ fontSize: 12, color: '#94a3b8' }}>List cleared. Click <strong>Show</strong> to restore.</p>
            ) : pastPostsForList.length === 0 ? (
              <p style={{ fontSize: 12, color: '#94a3b8' }}>
                {(pastFrom || pastUntil) ? 'No past posts in this date/time range. Try widening From / Until.' : 'No published posts this month.'}
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 440, overflowY: 'auto', paddingRight: 6 }}>
                {[...pastPostsForList].sort((a, b) => {
                  const da = a.dateTime || a.date || '';
                  const db = b.dateTime || b.date || '';
                  return db.localeCompare(da);
                }).map((p, i) => {
                  const rowPc = PLATFORMS.find((x) => x.id === (p.platform || '').toLowerCase());
                  return (
                    <div key={i} style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                        {rowPc && <PlatformIcon platform={rowPc} size={18} />}
                        <span style={{
                          background: PLATFORM_COLORS[p.platform] || '#6366f1',
                          color: '#fff', borderRadius: 20, padding: '2px 8px', fontSize: 10, fontWeight: 700, textTransform: 'capitalize',
                        }}>{rowPc?.label || p.platform}</span>
                        <span style={{ background: '#f1f5f9', color: '#475569', borderRadius: 20, padding: '2px 8px', fontSize: 10, fontWeight: 600, textTransform: 'capitalize' }}>
                          {(p.mediaType || p.postType || 'post').toString()}
                        </span>
                        {(p.dateTime || p.date) && (
                          <span style={{ marginLeft: 'auto', fontSize: 10, color: '#94a3b8' }}>
                            {p.dateTime ? parseUtcDate(p.dateTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : p.date}
                          </span>
                        )}
                      </div>
                      <p style={{ margin: 0, fontSize: 12, color: '#475569', lineHeight: 1.5,
                                   overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {p.caption || <em style={{ color: '#94a3b8' }}>No caption</em>}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Upcoming Scheduled Posts */}
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: '#f59e0b' }}>⏰</span> Upcoming Scheduled{' '}
              <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400 }}>
                ({upcomingScheduledFiltered.length}
                {(schedFrom || schedUntil) && upcomingScheduledForList.length !== upcomingScheduledFiltered.length
                  ? ` of ${upcomingScheduledForList.length}`
                  : ''})
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: '#64748b' }}>From</span>
              <input
                type="datetime-local"
                value={schedFrom}
                onChange={(e) => setSchedFrom(e.target.value)}
                style={{ fontSize: 11, padding: '4px 8px', borderRadius: 8, border: '1px solid #e2e8f0', color: '#334155' }}
              />
              <span style={{ fontSize: 10, fontWeight: 600, color: '#64748b' }}>Until</span>
              <input
                type="datetime-local"
                value={schedUntil}
                onChange={(e) => setSchedUntil(e.target.value)}
                style={{ fontSize: 11, padding: '4px 8px', borderRadius: 8, border: '1px solid #e2e8f0', color: '#334155' }}
              />
            </div>
            {upcomingScheduledForList.length === 0 ? (
              <p style={{ fontSize: 12, color: '#94a3b8' }}>No scheduled posts this month.</p>
            ) : upcomingScheduledFiltered.length === 0 ? (
              <p style={{ fontSize: 12, color: '#94a3b8' }}>
                {(schedFrom || schedUntil) ? 'No scheduled posts in this date/time range. Adjust From / Until (e.g. include the run time).' : 'No scheduled posts this month.'}
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 440, overflowY: 'auto', paddingRight: 6 }}>
                {[...upcomingScheduledFiltered].sort((a, b) => {
                  const da = a.dateTime || a.scheduledAt || a.date || '';
                  const db = b.dateTime || b.scheduledAt || b.date || '';
                  return da.localeCompare(db);
                }).map((j, i) => {
                  const rowPc = PLATFORMS.find((x) => x.id === (j.platform || '').toLowerCase());
                  const jobId = j.jobId ?? j.id;
                  const rowKey = jobId != null ? `job-${jobId}` : `up-${i}-${j.platform || ''}`;
                  return (
                    <div key={rowKey} style={{ background: '#fff', borderRadius: 10, border: '1px solid #fde68a', padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                        {rowPc && <PlatformIcon platform={rowPc} size={18} />}
                        <span style={{
                          background: PLATFORM_COLORS[j.platform] || '#f59e0b',
                          color: '#fff', borderRadius: 20, padding: '2px 8px', fontSize: 10, fontWeight: 700, textTransform: 'capitalize',
                        }}>{rowPc?.label || j.platform}</span>
                        <span style={{ background: '#fff7ed', color: '#c2410c', borderRadius: 20, padding: '2px 8px', fontSize: 10, fontWeight: 600 }}>
                          ⏰ {j.status || 'SCHEDULED'}
                        </span>
                        {(j.dateTime || j.scheduledAt || j.date) && (
                          <span style={{ marginLeft: 'auto', fontSize: 10, color: '#94a3b8' }}>
                            {j.dateTime
                              ? parseUtcDate(j.dateTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                              : j.date}
                          </span>
                        )}
                      </div>
                      <p style={{ margin: 0, fontSize: 12, color: '#475569', lineHeight: 1.5,
                                   overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {j.caption || <em style={{ color: '#94a3b8' }}>No caption</em>}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        </>
      )}

      <ComposePostModal
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        onPosted={loadCalendar}
      />

      {calTip && typeof document !== 'undefined' && (
        <div
          style={{
            position: 'fixed',
            left: Math.max(12, Math.min(calTip.left, window.innerWidth - 292)),
            top: calTip.top,
            transform: 'translate(-50%, calc(-100% - 8px))',
            zIndex: 20000,
            width: 280,
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 12,
            boxShadow: '0 12px 40px rgba(15,23,42,0.18)',
            padding: '12px 14px',
            pointerEvents: 'none',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            {calTip.pc ? <PlatformIcon platform={calTip.pc} size={28} /> : <span style={{ fontSize: 22 }}>📱</span>}
            <div>
              <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>
                {calTip.pc?.label || calTip.item.platform || 'Platform'}
              </div>
              <div style={{ fontSize: 11, color: '#64748b' }}>
                {calTip.kind === 'scheduled' ? 'Scheduled / pending' : 'Published'} ·{' '}
                <strong style={{ color: '#334155' }}>
                  {String(calTip.media).replace(/^\w/, (c) => c.toUpperCase())}
                </strong>
              </div>
            </div>
          </div>
          {(calTip.item.dateTime || calTip.item.date) && (
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>
              {calTip.item.dateTime
                ? parseUtcDate(calTip.item.dateTime).toLocaleString()
                : calTip.item.date}
            </div>
          )}
          <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.5, maxHeight: 100, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {calTip.item.caption || <em style={{ color: '#94a3b8' }}>No caption</em>}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Competitor Analysis ─────────────────────────────────────── */

/**
 * Parse a raw YouTube input (URL, @handle, channel name, or accidentally-concatenated URLs)
 * into a single clean search term for the backend.
 */
function parseYouTubeInput(raw) {
  if (!raw) return '';
  const trimmed = raw.trim();

  // Split on "https://" to handle accidentally concatenated URLs; take the first segment
  const urlParts = trimmed.split(/https?:\/\//);
  const firstSegment = urlParts.length > 1
    ? 'https://' + urlParts[1].split('https://')[0]  // keep only the first URL
    : trimmed;

  // Extract from YouTube URL
  if (/youtube\.com/i.test(firstSegment)) {
    // /@handle
    const handleMatch = firstSegment.match(/\/@([A-Za-z0-9_\-.]+)/);
    if (handleMatch) return '@' + handleMatch[1];
    // /channel/UCxxxxxx
    const channelMatch = firstSegment.match(/\/channel\/(UC[A-Za-z0-9_-]+)/);
    if (channelMatch) return channelMatch[1];
    // /c/name or /user/name
    const nameMatch = firstSegment.match(/\/(?:c|user)\/([^/?]+)/);
    if (nameMatch) return decodeURIComponent(nameMatch[1]);
  }

  // Already a @handle or channel ID
  return firstSegment.trim();
}

function CompetitorTab({ authHeaders }) {
  const [query,        setQuery]        = useState('');
  const [searching,    setSearching]    = useState(false);
  const [channels,     setChannels]     = useState(null);
  const [selectedCh,   setSelectedCh]   = useState(null);
  const [analysis,     setAnalysis]     = useState(null);
  const [loadAnalysis, setLoadAnalysis] = useState(false);
  const [ytError,      setYtError]      = useState('');

  // Cleaned query shown as a hint when user pastes a YouTube URL
  const cleanedQuery = useMemo(() => parseYouTubeInput(query), [query]);
  const isUrlInput   = /^https?:\/\//i.test(query.trim());

  // ── YouTube functions ─────────────────────────────────────────
  const doSearch = async () => {
    const q = cleanedQuery || query.trim();
    if (!q) return;
    setSearching(true); setChannels(null); setSelectedCh(null); setAnalysis(null); setYtError('');
    try {
      const res = await fetch(`${API}/api/analytics/competitors/youtube?query=${encodeURIComponent(q)}`,
        { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) { setYtError(data.error || 'Search failed'); return; }
      setChannels(data.channels || []);
    } catch {
      setYtError('Search failed. Make sure your YouTube account is connected.');
    } finally {
      setSearching(false);
    }
  };

  const doAnalyze = async (channelId) => {
    setSelectedCh(channelId); setLoadAnalysis(true); setAnalysis(null); setYtError('');
    try {
      const res = await fetch(`${API}/api/analytics/competitors/youtube/${channelId}`,
        { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) { setYtError(data.error || 'Analysis failed'); return; }
      setAnalysis(data);
    } catch {
      setYtError('Analysis failed.');
    } finally {
      setLoadAnalysis(false);
    }
  };

  return (
    <div>
          <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
            Search any YouTube channel to see their stats, recent videos, and engagement.
            <strong style={{ color: '#e74c3c' }}> Requires YouTube account connected.</strong>
          </p>

          {/* Search bar */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && doSearch()}
                placeholder="Channel name, @handle, or paste a YouTube URL"
                style={{
                  flex: 1, padding: '10px 14px',
                  border: `1.5px solid ${isUrlInput ? '#818cf8' : '#e2e8f0'}`,
                  borderRadius: 10, fontSize: 13, color: '#1e293b', outline: 'none',
                }}
              />
              <button onClick={doSearch} disabled={searching || !query.trim()} style={{
                padding: '10px 20px', background: '#6366f1', color: '#fff', border: 'none',
                borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                opacity: searching || !query.trim() ? 0.6 : 1,
              }}>
                {searching ? '⏳' : '🔍 Search'}
              </button>
            </div>
            {isUrlInput && cleanedQuery && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#6366f1', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>🔗 YouTube URL detected — will search as:</span>
                <strong>{cleanedQuery}</strong>
              </div>
            )}
            {isUrlInput && !cleanedQuery && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#dc2626' }}>
                Could not extract a channel name from this URL. Try pasting just the @handle or channel name.
              </div>
            )}
          </div>

          {ytError && <div style={{ background: '#fef2f2', color: '#dc2626', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>{ytError}</div>}

          {channels && channels.length === 0 && !searching && (
            <p style={{ color: '#94a3b8', fontSize: 13 }}>No channels found. Try a different search term or paste the full YouTube channel URL.</p>
          )}
          {channels && channels.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
              {channels.map(ch => (
                <div key={ch.channelId}
                  onClick={() => doAnalyze(ch.channelId)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    background: selectedCh === ch.channelId ? '#ede9fe' : '#fff',
                    border: `1.5px solid ${selectedCh === ch.channelId ? '#6366f1' : '#e2e8f0'}`,
                    borderRadius: 12, padding: '12px 16px', cursor: 'pointer', transition: 'all 0.15s',
                  }}>
                  {ch.thumbnailUrl && (
                    <img src={ch.thumbnailUrl} alt={ch.title} style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 2 }}>{ch.title}</div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, color: '#64748b' }}>👥 {ch.hiddenSubscribers ? 'Hidden' : fmtNum(ch.subscriberCount)} subs</span>
                      <span style={{ fontSize: 12, color: '#64748b' }}>🎬 {fmtNum(ch.videoCount)} videos</span>
                      <span style={{ fontSize: 12, color: '#64748b' }}>👁 {fmtNum(ch.viewCount)} views</span>
                    </div>
                  </div>
                  <span style={{ color: '#6366f1', fontSize: 13, fontWeight: 600 }}>Analyze →</span>
                </div>
              ))}
            </div>
          )}

          {loadAnalysis && <div style={s.loadingRow}><div style={s.spinner} /> Loading analysis…</div>}
          {analysis && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 16, padding: '20px 24px', marginBottom: 16 }}>
                {analysis.channel?.thumbnailUrl && (
                  <img src={analysis.channel.thumbnailUrl} alt="" style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover' }} />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 800, fontSize: 18, color: '#1e293b', marginBottom: 4 }}>{analysis.channel?.title}</div>
                  {analysis.channel?.description && (
                    <div style={{ fontSize: 12, color: '#64748b', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {analysis.channel.description}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                {[
                  ['👥 Subscribers', analysis.channel?.hiddenSubscribers ? 'Hidden' : fmtNum(analysis.channel?.subscriberCount)],
                  ['🎬 Total Videos',    fmtNum(analysis.channel?.videoCount)],
                  ['👁 Total Views',     fmtNum(analysis.channel?.viewCount)],
                  ['📊 Avg Views/Video', fmtNum(analysis.avgViewsPerVideo)],
                  ['❤️ Avg Likes/Video', fmtNum(analysis.avgLikesPerVideo)],
                  ['💬 Avg Comments',    fmtNum(analysis.avgCommentsPerVideo)],
                  ['📈 Avg Engagement',  `${analysis.avgEngagementRate}%`],
                ].map(([label, value]) => (
                  <div key={label} style={{ background: '#f8fafc', borderRadius: 12, padding: '12px 16px', minWidth: 120, flex: '1 1 120px', textAlign: 'center' }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#1e293b' }}>{value}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{label}</div>
                  </div>
                ))}
              </div>
              <div style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 16, overflow: 'hidden' }}>
                <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', fontWeight: 700, fontSize: 14, color: '#1e293b' }}>Recent Videos</div>
                {(analysis.recentVideos || []).map((v, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 20px', borderBottom: i < analysis.recentVideos.length - 1 ? '1px solid #f1f5f9' : 'none',
                  }}>
                    {v.thumbnailUrl && <img src={v.thumbnailUrl} alt="" style={{ width: 72, height: 40, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', marginBottom: 4 }}>{v.title}</div>
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, color: '#64748b' }}>👁 {fmtNum(v.viewCount)}</span>
                        <span style={{ fontSize: 11, color: '#64748b' }}>❤️ {fmtNum(v.likeCount)}</span>
                        <span style={{ fontSize: 11, color: '#64748b' }}>💬 {fmtNum(v.commentCount)}</span>
                        <span style={{ fontSize: 11, color: '#6366f1', fontWeight: 600 }}>{v.engagementRate}% eng.</span>
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>
                      {v.publishedAt ? parseUtcDate(v.publishedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
    </div>
  );
}

/* ── Section Error Boundary ──────────────────────────────────── */
class SectionErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('Growth Planner section crashed:', error, info);
  }
  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12,
          padding: '16px 20px', color: '#991b1b', fontSize: 13, lineHeight: 1.5,
          marginBottom: 16,
        }}>
          <strong>This section couldn't render for the selected platform.</strong>{' '}
          Try a different platform or refresh. Other sections below should still work.
        </div>
      );
    }
    return this.props.children;
  }
}

/* ── Main Component ──────────────────────────────────────────── */
export default function DeepAnalytics() {
  const { authHeaders } = useAuth();

  const [platform,     setPlatform]     = useState('all');
  const [days,         setDays]         = useState(30);
  const [history,      setHistory]      = useState(null);
  const [bestTime,     setBestTime]     = useState(null);
  const [postPerf,     setPostPerf]     = useState(null);
  const [loadHistory,  setLoadHistory]  = useState(true);
  const [loadBestTime, setLoadBestTime] = useState(true);
  const [loadPerf,     setLoadPerf]     = useState(true);
  const [snapping,     setSnapping]     = useState(false);
  const [snapMsg,      setSnapMsg]      = useState('');
  const platformForApi = platform === 'all' ? 'instagram' : platform;

  // Only accept 2xx JSON — 4xx/5xx error bodies were being stored as state and
  // breaking downstream renders (blank-page React crash when switching platform).
  const safeJson = (r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)));

  const fetchHistory = useCallback(() => {
    setLoadHistory(true);
    fetch(`${API}/api/analytics/history?platform=${platformForApi}&days=${days}`, { headers: authHeaders() })
      .then(safeJson)
      .then((data) => setHistory(data && typeof data === 'object' ? data : null))
      .catch(() => setHistory(null))
      .finally(() => setLoadHistory(false));
  }, [platformForApi, days, authHeaders]);

  const fetchBestTime = useCallback(() => {
    setLoadBestTime(true);
    const q = new URLSearchParams({ platform: platformForApi });
    fetch(`${API}/api/analytics/best-time?${q}`, { headers: authHeaders() })
      .then(safeJson)
      .then((data) => setBestTime(data && typeof data === 'object' ? data : null))
      .catch(() => setBestTime(null))
      .finally(() => setLoadBestTime(false));
  }, [platformForApi, authHeaders]);

  const fetchPerformance = useCallback(() => {
    setLoadPerf(true);
    const q = new URLSearchParams({ platform: platformForApi });
    fetch(`${API}/api/analytics/post-performance?${q}`, { headers: authHeaders() })
      .then(safeJson)
      .then((data) => setPostPerf(data && typeof data === 'object' ? data : null))
      .catch(() => setPostPerf(null))
      .finally(() => setLoadPerf(false));
  }, [platformForApi, authHeaders]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);
  useEffect(() => { fetchBestTime(); }, [fetchBestTime]);
  useEffect(() => { fetchPerformance(); }, [fetchPerformance]);

  const refreshSnapshot = async () => {
    setSnapping(true);
    try {
      const res = await fetch(`${API}/api/analytics/snapshot/run?platform=${platformForApi}`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (data.status === 'error') {
        setSnapMsg('❌ ' + (data.message || 'Snapshot failed'));
        setTimeout(() => setSnapMsg(''), 5000);
        return;
      }
      setSnapMsg('Snapshot saved! Refreshing chart…');
      setTimeout(() => { setSnapMsg(''); fetchHistory(); }, 1500);
    } catch {
      setSnapMsg('❌ Network error — snapshot failed');
      setTimeout(() => setSnapMsg(''), 3000);
    } finally {
      setSnapping(false);
    }
  };

  const platformConfig = PLATFORMS.find(p => p.id === platform) || {
    id: 'all',
    label: 'All Platforms',
    color: '#6366f1',
    emoji: '🌐',
    logo: 'globe',
  };

  const bestTimeResolved = useMemo(() => {
    if (!bestTime || typeof bestTime !== 'object') return null;
    return resolveBestTimeView(bestTime);
  }, [bestTime]);

  return (
    <div style={s.wrap} translate="no">

      {/* ── Jump links (all sections below on one page) — hashes are normalized in App.js to tab "trends" */}
      <div
        role="navigation"
        aria-label="Section shortcuts"
        style={{
          display: 'flex',
          gap: 10,
          rowGap: 8,
          flexWrap: 'wrap',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: '#94a3b8',
            marginRight: 4,
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          Jump to
        </span>
        {SECTION_TABS.map(tab => (
          <a
            key={tab.id}
            href={`#trends-${tab.id}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              fontSize: 12,
              fontWeight: 600,
              color: '#6366f1',
              textDecoration: 'none',
              padding: '6px 12px',
              borderRadius: 999,
              border: '1px solid #e0e7ff',
              background: '#f5f3ff',
              flexShrink: 0,
              cursor: 'pointer',
            }}
          >
            <span style={{ opacity: 0.85 }}>{tab.serial}.</span> {tab.label}
          </a>
        ))}
        <div style={{ flexShrink: 0, marginLeft: 'auto' }}>
          <PlatformScopeMenu value={platform} onChange={setPlatform} />
        </div>
      </div>

      {/* ── FOLLOWER GROWTH ── */}
      <SectionErrorBoundary resetKey={`growth-${platform}`}>
      <section id="trends-growth" style={{ scrollMarginTop: 12 }}>
        <div style={s.card}>
          <div style={s.cardHeader}>
            <h3 style={s.cardTitle}>👥 Follower Growth</h3>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <select value={days} onChange={e => setDays(Number(e.target.value))} style={s.select}>
                <option value={7}>7 days</option>
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
              </select>
              <button
                style={{ ...s.refreshBtn, background: platformConfig.color, opacity: snapping ? 0.7 : 1 }}
                onClick={refreshSnapshot}
                disabled={snapping}
              >{snapping ? '⏳' : '🔄'} Refresh Now</button>
            </div>
          </div>
          {snapMsg && <div style={s.snapMsg}>{snapMsg}</div>}
          {history && history.daysSinceLastSnapshot > 2 && (
            <div style={{
              background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8,
              padding: '10px 14px', fontSize: 12, color: '#92400e', marginBottom: 12,
              lineHeight: 1.6,
            }}>
              ⚠️ Follower data is outdated — last snapshot was <strong>{history.lastSnapshotDate}</strong> ({history.daysSinceLastSnapshot} days ago).
              Your <strong>{platformForApi}</strong> token may have expired.
              Go to <strong>Social Connect</strong> to reconnect it, then click <strong>Refresh Now</strong>.
            </div>
          )}
          {loadHistory ? (
            <div style={s.loadingRow}><div style={s.spinner} /> Loading…</div>
          ) : (
            <>
              <div style={s.chartStretch}>
                <LineChart
                  key={`followers-chart-${platform}`}
                  data={history?.dataPoints || []}
                  color={platformConfig.color}
                  width={960}
                  height={200}
                />
              </div>
              {history?.dataPoints?.length > 0 && (
                <div style={s.statRow}>
                  <div style={s.stat}>
                    <span style={s.statNum}>{(history.dataPoints[history.dataPoints.length - 1]?.followers || 0).toLocaleString()}</span>
                    <span style={s.statLabel}>Current Followers</span>
                  </div>
                  {history.dataPoints.length >= 2 && (
                    <div style={s.stat}>
                      <span style={{ ...s.statNum, color: '#10b981' }}>
                        {((history.dataPoints[history.dataPoints.length - 1]?.followers || 0)
                          - (history.dataPoints[0]?.followers || 0)).toLocaleString()}
                      </span>
                      <span style={s.statLabel}>Growth ({days}d)</span>
                    </div>
                  )}
                  <div style={s.stat}>
                    <span style={s.statNum}>{history.dataPoints[history.dataPoints.length - 1]?.engagementRate || '—'}%</span>
                    <span style={s.statLabel}>Engagement Rate</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>
      </SectionErrorBoundary>

      {/* ── BEST TIME ── */}
      <SectionErrorBoundary resetKey={`besttime-${platform}`}>
      <section id="trends-besttime" style={{ scrollMarginTop: 12 }}>
        <div style={s.card}>
          <h3 style={s.cardTitle}>⏰ Your posting times — {platformConfig.label}</h3>
          {loadBestTime ? (
            <div style={s.loadingRow}><div style={s.spinner} /> Loading…</div>
          ) : !bestTime ? (
            <p style={{ color: '#94a3b8', fontSize: 13 }}>Could not load best time data.</p>
          ) : !bestTimeResolved ? (
            // Wrapped with a stable key so React cleanly unmounts the empty-guide
            // tree when switching to the resolved tree (and vice versa). Prevents
            // the "removeChild: not a child of this node" reconciliation crash
            // when these two shape-incompatible branches swap during a
            // platform-change re-render.
            <div key={`${platform}-bt-empty`}>
              <BestTimeEmptyGuide platformLabel={platformConfig.label} platformColor={platformConfig.color} />
            </div>
          ) : (
            <div key={`${platform}-bt-resolved`}>
              <BestTimeGuidancePanel
                resolved={bestTimeResolved}
                platformLabel={platformConfig.label}
                platformColor={platformConfig.color}
              />
              <PlatformBestTimeCards bestTime={bestTime} />
              <PostHeatmap
                key={`${platform}-heatmap`}
                resolved={bestTimeResolved}
                postsDetail={bestTime.postsDetail || []}
                platformLabel={platformConfig.label}
              />
            </div>
          )}
        </div>
      </section>
      </SectionErrorBoundary>

      {/* ── POST TYPE BREAKDOWN ── */}
      <SectionErrorBoundary resetKey={`breakdown-${platform}`}>
      <section id="trends-breakdown" style={{ scrollMarginTop: 12 }}>
        <div style={s.card}>
          <h3 style={s.cardTitle}>📊 Post Type Breakdown</h3>
          {loadPerf ? (
            <div style={s.loadingRow}><div style={s.spinner} /> Loading…</div>
          ) : postPerf && Object.keys(postPerf.byType || {}).length > 0 ? (
            <div style={s.perfRow}>
              <div style={{ flex: '1 1 300px', minWidth: 0 }}>
                <TypeTowersChart
                  key={`towers-${platform}`}
                  postPerf={postPerf}
                  platformColor={platformConfig.color}
                />
              </div>
              <div style={{ flex: '2 1 360px', minWidth: 0 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {Object.entries(postPerf.byPlatform || {}).map(([plat, count]) => {
                    const pc  = PLATFORMS.find(p => p.id === plat);
                    const max = Math.max(...Object.values(postPerf.byPlatform || {}));
                    return (
                      <div key={plat} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 12, width: 100, flexShrink: 0, color: '#475569', display: 'flex', alignItems: 'center', gap: 5 }}>
                          {pc ? <PlatformIcon platform={pc} size={14} /> : null}
                          {pc?.label || plat}
                        </span>
                        <div style={{ flex: 1, height: 12, minWidth: 80, background: '#f1f5f9', borderRadius: 6, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${(count / max) * 100}%`, background: pc?.color || '#6366f1', borderRadius: 6 }} />
                        </div>
                        <span style={{ fontSize: 12, color: '#64748b', width: 28, flexShrink: 0, textAlign: 'right' }}>{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <p style={{ color: '#94a3b8', fontSize: 13 }}>No published posts yet. Start publishing to see performance data.</p>
          )}
        </div>
      </section>
      </SectionErrorBoundary>

      {/* ── COMPETITOR ── */}
      <SectionErrorBoundary resetKey={`competitor-${platform}`}>
      <section id="trends-competitor" style={{ scrollMarginTop: 12 }}>
        <div style={s.card}>
          <h3 style={s.cardTitle}>🔍 YouTube Competitor Analysis</h3>
          {/* key forces clean remount on platform change — avoids internal-state
              staleness and sidesteps reconciliation for this big subtree. */}
          <CompetitorTab key={`competitor-${platform}`} authHeaders={authHeaders} />
        </div>
      </section>
      </SectionErrorBoundary>

      {/* ── CALENDAR ── */}
      <SectionErrorBoundary resetKey={`calendar-${platform}`}>
      <section id="trends-calendar" style={{ scrollMarginTop: 12 }}>
        <div style={s.card}>
          <h3 style={s.cardTitle}>📅 Posts Calendar</h3>
          <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>
            View all your published and scheduled posts by month. The <strong>Best Time</strong> section above shows which days and hours you post most often.
          </p>
          <TrendsCalendar key={`calendar-${platform}`} authHeaders={authHeaders} />
        </div>
      </section>
      </SectionErrorBoundary>

    </div>
  );
}

/* ── Styles ── */
const s = {
  wrap: {
    width: '100%', maxWidth: '100%', margin: 0,
    // Top padding raised to clear the App Launcher (22px tall, hangs 18px below topbar).
    padding: '28px 12px 24px', boxSizing: 'border-box',
    fontFamily: "'Inter', -apple-system, sans-serif",
  },
  chartStretch: { width: '100%', minHeight: 200 },
  perfRow: { display: 'flex', flexWrap: 'wrap', gap: 40, alignItems: 'flex-start', justifyContent: 'space-between' },
  tabs:      { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 },
  tab: {
    padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
    cursor: 'pointer', transition: 'all 0.15s',
  },
  card: {
    background: '#fff', borderRadius: 16,
    boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
    padding: '24px', marginBottom: 28,
  },
  cardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 16 },
  cardTitle:  { fontSize: 16, fontWeight: 700, color: '#1e293b', margin: 0 },
  select: {
    padding: '6px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8,
    fontSize: 12, color: '#475569', background: '#f8fafc', cursor: 'pointer',
  },
  refreshBtn: {
    border: 'none', color: '#fff', borderRadius: 8,
    padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  snapMsg:    { background: '#ede9fe', color: '#5b21b6', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 },
  loadingRow: { display: 'flex', alignItems: 'center', gap: 10, color: '#94a3b8', fontSize: 13, padding: '20px 0' },
  spinner: {
    width: 20, height: 20, border: '2px solid #e2e8f0',
    borderTop: '2px solid #6366f1', borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  statRow:   { display: 'flex', gap: 24, marginTop: 16, flexWrap: 'wrap' },
  stat:      { display: 'flex', flexDirection: 'column', gap: 2 },
  statNum:   { fontSize: 20, fontWeight: 700, color: '#1e293b' },
  statLabel: { fontSize: 11, color: '#94a3b8' },
  navBtn: {
    background: '#f1f5f9', border: 'none', borderRadius: 8,
    padding: '6px 14px', fontSize: 16, cursor: 'pointer', color: '#475569',
  },
};
